import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from './api';
import useAuth from '../hooks/useAuth';
import { isSuper } from '../utils/canDo';

const BODY_PART_OPTIONS = [
  { key: 'lowerback',  label: 'Lower Back' },
  { key: 'middleback', label: 'Middle Back' },
  { key: 'upperback',  label: 'Upper Back' },
  { key: 'cervical',   label: 'Cervical / Neck' },
  { key: 'hip',        label: 'Hip' },
  { key: 'knee',       label: 'Knee' },
  { key: 'shoulder',   label: 'Shoulder' },
  { key: 'ankle',      label: 'Ankle' },
];

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function extractYoutubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return { id: u.pathname.slice(1).split('/')[0], isShort: false };
    if (u.hostname.includes('youtube.com')) {
      const shortsMatch = u.pathname.match(/\/shorts\/([a-zA-Z0-9_-]+)/);
      if (shortsMatch) return { id: shortsMatch[1], isShort: true };
      if (u.searchParams.get('v')) return { id: u.searchParams.get('v'), isShort: false };
    }
  } catch {}
  return null;
}

function extractDriveFileId(url) {
  try {
    const m = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    if (m) return m[1];
    const u = new URL(url);
    if (u.searchParams.get('id')) return u.searchParams.get('id');
  } catch {}
  return null;
}

var DURATION_OPTIONS = [
  { value: 3,   label: '72 hrs' },
  { value: 10,  label: '10 days' },
  { value: 20,  label: '20 days' },
  { value: 30,  label: '30 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
];

function getAssignedCards(assignedPatients) {
  if (!assignedPatients || assignedPatients.length === 0) return [];
  return assignedPatients.map(a => typeof a === 'string' ? a : a.patientCard).filter(Boolean);
}

function getAssignedDate(assignedPatients, card) {
  if (!assignedPatients) return null;
  const entry = assignedPatients.find(a => typeof a !== 'string' && a.patientCard === card);
  return entry?.assignedAt || null;
}

function getAssignmentEntry(assignedPatients, card) {
  if (!assignedPatients) return null;
  return assignedPatients.find(a => typeof a !== 'string' && a.patientCard === card) || null;
}

function fmtExpiry(validUntil) {
  if (!validUntil) return null;
  var vu = new Date(validUntil);
  var now = new Date();
  var ms = vu.getTime() - now.getTime();
  if (ms <= 0) return 'Expired';
  var days = Math.ceil(ms / 86400000);
  if (days <= 1) return Math.ceil(ms / 3600000) + 'h left';
  return days + 'd left';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function Resources() {
  const { auth } = useAuth();
  const canSeeEncryption = isSuper(auth);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── Top-level tab: 'videos' | 'files' ──
  const [mainTab, setMainTab] = useState('videos');

  // ── Channel management ──
  const [channels, setChannels]           = useState([]);
  const [selectedChIds, setSelectedChIds] = useState(new Set());
  const [channelUrl, setChannelUrl]       = useState('');
  const [addingChannel, setAddingChannel] = useState(false);
  const [deletingChId, setDeletingChId]   = useState(null);

  // ── Playlist management ──
  const [playlists, setPlaylists]           = useState([]);
  const [selectedPlId, setSelectedPlId]     = useState(null);
  const [playlistUrl, setPlaylistUrl]       = useState('');
  const [addingPlaylist, setAddingPlaylist] = useState(false);
  const [playlistImportMsg, setPlaylistImportMsg] = useState('');
  const [viewMode, setViewMode]             = useState('videos'); // 'videos' | 'playlists'

  // ── Playlist player ──
  const [playlistQueue, setPlaylistQueue] = useState([]);
  const [playlistIdx, setPlaylistIdx]     = useState(0);

  // ── Files & Docs ──
  const [resFiles, setResFiles]             = useState([]);
  const [loadingFiles, setLoadingFiles]     = useState(true);
  const [fileCategory, setFileCategory]     = useState('all');
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [dupCheckResult, setDupCheckResult] = useState(null); // { exists, file, action: null|'replace'|'copy' }
  const [uploadDocFile, setUploadDocFile]   = useState(null);
  const [uploadDocTitle, setUploadDocTitle] = useState('');
  const [uploadDocKw, setUploadDocKw]       = useState('');
  const [uploadingDoc, setUploadingDoc]     = useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [showAddLinkModal, setShowAddLinkModal]   = useState(false);
  const [linkUrl, setLinkUrl]           = useState('');
  const [linkTitle, setLinkTitle]       = useState('');
  const [linkDesc, setLinkDesc]         = useState('');
  const [linkKeywords, setLinkKeywords] = useState('');
  const [addingLink, setAddingLink]     = useState(false);
  const [fileActionItem, setFileActionItem] = useState(null);
  const [fileActionTab, setFileActionTab]   = useState('info');
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const docFileRef = useRef(null);

  // ── Folder navigation ──
  const [currentFolderPath, setCurrentFolderPath] = useState('/');
  const [folders, setFolders]               = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName]   = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null); // { name, path }
  const [renameFolderName, setRenameFolderName] = useState('');
  const [showMoveModal, setShowMoveModal]   = useState(false);
  const [moveTargetPath, setMoveTargetPath] = useState('/');
  const [allFolders, setAllFolders]         = useState([]);
  const [folderActionMenu, setFolderActionMenu] = useState(null); // folder obj for context menu
  const [fileSortCol, setFileSortCol] = useState('date');  // 'name'|'type'|'size'|'date'|'by'
  const [fileSortDir, setFileSortDir] = useState('desc');  // 'asc'|'desc'

  // ── Channel videos (from YouTube) ──
  const [channelVideos, setChannelVideos] = useState([]);
  const [loadingYt, setLoadingYt]         = useState(true);

  // ── Saved resources (from DB) ──
  const [savedVideos,   setSavedVideos]   = useState([]);
  const [loadingSaved,  setLoadingSaved]  = useState(true);
  const [savingId,      setSavingId]      = useState(null);

  // ── Unified filter/search — multi-select: 'video','short' ──
  const [activeFilters, setActiveFilters] = useState(new Set(['video', 'short']));
  const [searchQuery, setSearchQuery]     = useState('');

  // ── Keywords for saving from channel grid ──
  const [saveKeywordsInput, setSaveKeywordsInput] = useState({});

  // ── Add modal ──
  const [showAddModal, setShowAddModal]   = useState(false);
  const [addTab, setAddTab]               = useState('channel');
  const [ytUrl,           setYtUrl]       = useState('');
  const [ytAdding,        setYtAdding]    = useState(false);
  const [ytKeywords,      setYtKeywords]  = useState('');
  const [driveUrl,        setDriveUrl]    = useState('');
  const [driveAdding,     setDriveAdding] = useState(false);
  const [driveTitle,      setDriveTitle]  = useState('');
  const [driveKeywords,   setDriveKeywords] = useState('');
  const [uploadFile,      setUploadFile]  = useState(null);
  const [uploading,       setUploading]   = useState(false);
  const [uploadTitle,     setUploadTitle] = useState('');
  const [uploadKeywords,  setUploadKeywords] = useState('');
  const fileRef = useRef(null);

  // ── Single-video action modal ──
  const [actionVideo, setActionVideo] = useState(null);
  const [actionTab, setActionTab]     = useState('info');

  // ── Assignment (shared by single + bulk) ──
  const [patientQuery,   setPatientQuery]   = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [searchingPt,    setSearchingPt]    = useState(false);
  const [assignLabel,    setAssignLabel]    = useState('recommended'); // mandatory | recommended | optional
  const [assignDuration, setAssignDuration] = useState(30); // days

  // ── Keywords editing ──
  const [kwInput, setKwInput] = useState('');

  // ── Inline video player ──
  const [playingVideo, setPlayingVideo] = useState(null);

  // ── MULTI-SELECT + BULK ──
  const [selectedIds, setSelectedIds] = useState(new Set()); // Set of _id strings
  const [bulkModal, setBulkModal]     = useState(null); // 'addKeywords'|'removeKeywords'|'assign'|'unassign'|'delete'
  const [bulkBusy, setBulkBusy]       = useState(false);
  const [bulkKwInput, setBulkKwInput] = useState('');
  const [bulkRemoveKws, setBulkRemoveKws] = useState(new Set()); // keywords marked for removal

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA FETCHING
  // ═══════════════════════════════════════════════════════════════════════════

  const fetchChannels = useCallback(async (autoSelect) => {
    try {
      const res = await api.get('/youtube/channels');
      const chs = res.data || [];
      setChannels(chs);
      if (autoSelect && chs.length > 0) {
        const myc = chs.find(c => (c.name || '').toLowerCase().includes('medicalyog'));
        setSelectedChIds(new Set([myc ? myc.channelId : chs[0].channelId]));
      }
    } catch { setChannels([]); }
  }, []);

  const fetchChannelVideos = useCallback(async (chIds) => {
    setLoadingYt(true);
    try {
      const params = {};
      if (chIds && chIds.length > 0) params.channelIds = chIds.join(',');
      const res = await api.get('/youtube/channel-videos', { params });
      setChannelVideos(res.data?.videos || []);
    } catch { setChannelVideos([]); }
    finally { setLoadingYt(false); }
  }, []);

  const fetchSavedVideos = useCallback(async () => {
    setLoadingSaved(true);
    try {
      const res = await api.get('/resource-videos');
      setSavedVideos(res.data || []);
    } catch { setSavedVideos([]); }
    finally { setLoadingSaved(false); }
  }, []);

  const fetchPlaylists = useCallback(async () => {
    try {
      const res = await api.get('/youtube/playlists');
      setPlaylists(res.data || []);
    } catch { setPlaylists([]); }
  }, []);

  const fetchResFiles = useCallback(async (folderPath) => {
    setLoadingFiles(true);
    try {
      const params = {};
      if (folderPath) params.folderPath = folderPath;
      const res = await api.get('/resource-files', { params });
      setResFiles(res.data || []);
    } catch { setResFiles([]); }
    finally { setLoadingFiles(false); }
  }, []);

  const fetchFolders = useCallback(async (parentPath) => {
    setLoadingFolders(true);
    try {
      const res = await api.get('/resource-files/folders', { params: { path: parentPath || '/' } });
      setFolders(res.data || []);
    } catch { setFolders([]); }
    finally { setLoadingFolders(false); }
  }, []);

  const fetchAllFolders = useCallback(async () => {
    try {
      // Recursively fetch all folders (for move modal picker)
      const result = [];
      const queue = ['/'];
      while (queue.length > 0) {
        const p = queue.shift();
        try {
          const res = await api.get('/resource-files/folders', { params: { path: p } });
          for (const f of (res.data || [])) {
            result.push(f);
            queue.push(f.path);
          }
        } catch { /* skip */ }
      }
      setAllFolders(result);
    } catch { setAllFolders([]); }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchChannels(true);
      // fetchChannelVideos will be triggered by selectedChIds change via the effect below
      fetchSavedVideos();
      fetchPlaylists();
      fetchResFiles('/');
      fetchFolders('/');
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch files + folders when navigating into a different folder
  useEffect(() => {
    if (mainTab === 'files') {
      fetchResFiles(fileSearchQuery ? undefined : currentFolderPath);
      fetchFolders(currentFolderPath);
    }
  }, [currentFolderPath, mainTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // When search query changes, fetch all files (global) or folder-scoped
  useEffect(() => {
    if (mainTab !== 'files') return;
    const timer = setTimeout(() => {
      fetchResFiles(fileSearchQuery ? undefined : currentFolderPath);
    }, 300);
    return () => clearTimeout(timer);
  }, [fileSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevSelectedRef = useRef(null);
  useEffect(() => {
    const key = Array.from(selectedChIds).sort().join(',');
    if (!key) return; // no channels selected yet
    if (prevSelectedRef.current === key) return;
    prevSelectedRef.current = key;
    fetchChannelVideos(Array.from(selectedChIds));
  }, [selectedChIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC key clears selection
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setSelectedIds(new Set()); setBulkModal(null); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // UNIFIED VIDEO LIST
  // ═══════════════════════════════════════════════════════════════════════════

  const allVideos = (() => {
    const map = new Map();
    for (const sv of savedVideos) {
      const key = sv.youtubeId || sv.driveFileId || sv._id;
      map.set(key, {
        _id: sv._id, youtubeId: sv.youtubeId || '', driveFileId: sv.driveFileId || '',
        source: sv.source || 'youtube', title: sv.title || '', description: sv.description || '',
        thumbnail: sv.thumbnail || (sv.youtubeId ? `https://i.ytimg.com/vi/${sv.youtubeId}/mqdefault.jpg` : ''),
        publishedAt: sv.publishedAt || sv.savedAt || '', videoType: sv.videoType || 'video',
        duration: sv.duration || 0, channelId: sv.channelId || '', keywords: sv.keywords || [],
        bodyParts: sv.bodyParts || [], assignedPatients: sv.assignedPatients || [],
        savedAt: sv.savedAt, savedBy: sv.savedBy, isSaved: true, mimeType: sv.mimeType, playlistId: sv.playlistId,
      });
    }
    for (const cv of channelVideos) {
      if (!cv.youtubeId || map.has(cv.youtubeId)) continue;
      map.set(cv.youtubeId, {
        youtubeId: cv.youtubeId, source: 'youtube', title: cv.title || '', description: cv.description || '',
        thumbnail: cv.thumbnail || `https://i.ytimg.com/vi/${cv.youtubeId}/mqdefault.jpg`,
        publishedAt: cv.publishedAt || '', videoType: cv.videoType || 'video',
        duration: cv.duration || 0, channelId: cv.channelId || '', keywords: cv.keywords || [],
        isSaved: false,
      });
    }
    return Array.from(map.values());
  })();

  const q = searchQuery.toLowerCase();
  const filteredVideos = allVideos.filter(v => {
    const type = v.videoType || 'video';
    if (!activeFilters.has(type)) return false;
    if (selectedPlId && v.playlistId !== selectedPlId) return false;
    if (!q) return true;
    return (v.title?.toLowerCase().includes(q) ||
            (v.keywords || []).some(k => k.toLowerCase().includes(q)) ||
            (v.bodyParts || []).some(b => b.toLowerCase().includes(q)));
  });

  const videoCount = allVideos.filter(v => (v.videoType || 'video') === 'video').length;
  const shortCount = allVideos.filter(v => v.videoType === 'short').length;
  const isAllSelected = activeFilters.has('video') && activeFilters.has('short') && viewMode === 'videos' && !selectedPlId;
  const loading = loadingYt && loadingSaved;
  const playlistCount = playlists.length;

  // Saved videos visible in current grid (for Select All)
  const visibleSavedIds = filteredVideos.filter(v => v.isSaved && v._id).map(v => v._id);
  const allVisibleSelected = visibleSavedIds.length > 0 && visibleSavedIds.every(id => selectedIds.has(id));

  // Live action video synced with savedVideos
  const liveActionVideo = actionVideo
    ? (allVideos.find(v => (v._id || v.youtubeId) === (actionVideo._id || actionVideo.youtubeId)) || actionVideo)
    : null;

  // Collect all keywords from selected videos (for remove keywords modal)
  const selectedVideoObjs = allVideos.filter(v => v._id && selectedIds.has(v._id));
  const allSelectedKeywords = [...new Set(selectedVideoObjs.flatMap(v => v.keywords || []))].sort();
  const selectedAssignedCount = selectedVideoObjs.filter(v => (v.assignedPatients || []).length > 0).length;

  // ═══════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) { if (next.size > 1) next.delete(type); }
      else next.add(type);
      return next;
    });
    setSelectedIds(new Set());
  };

  const selectAll = () => { setActiveFilters(new Set(['video', 'short'])); setSelectedIds(new Set()); setViewMode('videos'); setSelectedPlId(null); };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleSavedIds));
    }
  };

  const handleAddChannel = async () => {
    const url = channelUrl.trim();
    if (!url) return;
    setAddingChannel(true);
    try {
      const isUrl = url.includes('youtube.com') || url.includes('youtu.be');
      await api.post('/youtube/channels', isUrl ? { channelUrl: url } : { channelId: url });
      setChannelUrl('');
      await fetchChannels();
      setTimeout(() => fetchChannelVideos(selectedChIds.size > 0 ? Array.from(selectedChIds) : []), 2500);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add channel');
    } finally { setAddingChannel(false); }
  };

  const handleDeleteChannel = async (ch) => {
    if (deletingChId) return;
    setDeletingChId(ch._id);
    try {
      await api.delete(`/youtube/channels/${ch._id}`);
      setSelectedChIds(prev => { const n = new Set(prev); n.delete(ch.channelId); return n; });
      await fetchChannels();
      fetchChannelVideos();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove channel');
    } finally { setDeletingChId(null); }
  };

  const toggleChannelSelection = (chId) => {
    setSelectedChIds(prev => { const n = new Set(prev); if (n.has(chId)) n.delete(chId); else n.add(chId); return n; });
  };

  const handleSave = async (video) => {
    const kwStr = (saveKeywordsInput[video.youtubeId] || '').trim();
    if (!kwStr) { alert('Enter keywords before saving.'); return; }
    setSavingId(video.youtubeId);
    try {
      await api.post('/resource-videos', {
        youtubeId: video.youtubeId, title: video.title, description: video.description,
        thumbnail: video.thumbnail, publishedAt: video.publishedAt, keywords: kwStr,
        videoType: video.videoType || 'video',
      });
      setSaveKeywordsInput(prev => { const n = { ...prev }; delete n[video.youtubeId]; return n; });
      await fetchSavedVideos();
      setActionVideo(null);
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSavingId(null); }
  };

  const handleAddYoutubeUrl = async () => {
    const result = extractYoutubeId(ytUrl.trim());
    if (!result) { alert('Invalid YouTube URL'); return; }
    const { id: videoId, isShort } = result;
    if (savedVideos.some(sv => sv.youtubeId === videoId)) { alert('Already saved'); return; }
    if (!ytKeywords.trim()) { alert('Keywords are required.'); return; }
    setYtAdding(true);
    try {
      let title = 'YouTube Video', thumbnail = '';
      try {
        const oembed = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`);
        const data = await oembed.json();
        if (data.title) title = data.title;
        if (data.thumbnail_url) thumbnail = data.thumbnail_url;
      } catch {}
      await api.post('/resource-videos', {
        youtubeId: videoId, title, thumbnail, keywords: ytKeywords,
        videoType: isShort ? 'short' : undefined,
      });
      setYtUrl(''); setYtKeywords('');
      await fetchSavedVideos();
      setShowAddModal(false);
    } catch (err) { alert(err.response?.data?.error || 'Add failed'); }
    finally { setYtAdding(false); }
  };

  const handleAddDriveLink = async () => {
    const fileId = extractDriveFileId(driveUrl.trim());
    if (!fileId) { alert('Invalid Google Drive URL.'); return; }
    if (!driveKeywords.trim()) { alert('Keywords are required.'); return; }
    setDriveAdding(true);
    try {
      await api.post('/resource-videos/drive-link', { driveFileId: fileId, title: driveTitle.trim() || 'Drive Video', keywords: driveKeywords });
      setDriveUrl(''); setDriveTitle(''); setDriveKeywords('');
      await fetchSavedVideos();
      setShowAddModal(false);
    } catch (err) { alert(err.response?.data?.error || 'Add failed'); }
    finally { setDriveAdding(false); }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    if (!uploadKeywords.trim()) { alert('Keywords are required.'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', uploadFile);
      if (uploadTitle.trim()) formData.append('title', uploadTitle.trim());
      formData.append('keywords', uploadKeywords);
      await api.post('/resource-videos/upload', formData, { headers: { 'Content-Type': undefined }, timeout: 300000 });
      setUploadFile(null); setUploadTitle(''); setUploadKeywords('');
      if (fileRef.current) fileRef.current.value = '';
      await fetchSavedVideos();
      setShowAddModal(false);
    } catch (err) {
      if (err.response?.data?.error === 'GDRIVE_REAUTH') {
        alert('Google Drive is disconnected. Please ask an admin/owner to reconnect it from the Resources page.');
      } else {
        alert('Upload failed: ' + (err.response?.data?.error || err.message));
      }
    }
    finally { setUploading(false); }
  };

  const handleTagToggle = async (videoId, tag, currentTags) => {
    const next = currentTags.includes(tag) ? currentTags.filter(t => t !== tag) : [...currentTags, tag];
    try {
      const res = await api.put(`/resource-videos/${videoId}/bodyparts`, { bodyParts: next });
      setSavedVideos(prev => prev.map(v => v._id === videoId ? res.data : v));
    } catch {}
  };

  const handleKeywordsUpdate = async (videoId) => {
    try {
      const res = await api.put(`/resource-videos/${videoId}/keywords`, { keywords: kwInput });
      setSavedVideos(prev => prev.map(v => v._id === videoId ? res.data : v));
      setActionTab('info');
    } catch {}
  };

  const handleDeleteVideo = async (video) => {
    try {
      await api.delete(`/resource-videos/${video._id}`);
      setSavedVideos(prev => prev.filter(v => v._id !== video._id));
      setActionVideo(null);
    } catch {}
  };

  const searchPatients = async (pq) => {
    setPatientQuery(pq);
    if (pq.length < 3) { setPatientResults([]); return; }
    setSearchingPt(true);
    try {
      const res = await api.get(`/patients/profiles/search/${encodeURIComponent(pq)}`);
      setPatientResults((res.data || []).slice(0, 10));
    } catch { setPatientResults([]); }
    finally { setSearchingPt(false); }
  };

  const handleAssign = async (videoId, patientCard, label) => {
    try {
      const res = await api.put(`/resource-videos/${videoId}/assign`, { patientCard, label: label || assignLabel, durationDays: assignDuration });
      setSavedVideos(prev => prev.map(v => v._id === videoId ? res.data : v));
    } catch {}
  };

  const handleUnassign = async (videoId, patientCard) => {
    try {
      const res = await api.put(`/resource-videos/${videoId}/unassign`, { patientCard });
      setSavedVideos(prev => prev.map(v => v._id === videoId ? res.data : v));
    } catch {}
  };

  const handleExtendVideo = async (videoId, patientCard, days) => {
    try {
      const res = await api.put(`/resource-videos/${videoId}/extend`, { patientCard, durationDays: days });
      setSavedVideos(prev => prev.map(v => v._id === videoId ? res.data : v));
    } catch {}
  };

  const playVideo = (v) => {
    if (v.source === 'drive' && v._id) {
      setPlayingVideo({ ...v, streamUrl: (process.env.REACT_APP_URL || '') + '/resource-videos/stream/' + v._id });
    } else if (v.youtubeId) {
      setPlayingVideo(v);
    }
  };

  const openActionModal = (v) => {
    setActionVideo(v);
    setActionTab(v.isSaved ? 'info' : 'save');
    setPatientQuery(''); setPatientResults([]);
    setKwInput((v.keywords || []).join(', '));
  };

  // ── BULK ACTIONS ──
  const bulkFinish = async () => {
    await fetchSavedVideos();
    setSelectedIds(new Set());
    setBulkModal(null);
    setBulkBusy(false);
    setBulkKwInput('');
    setBulkRemoveKws(new Set());
    setPatientQuery(''); setPatientResults([]);
  };

  const handleBulkAddKeywords = async () => {
    if (!bulkKwInput.trim()) return;
    setBulkBusy(true);
    try {
      await api.put('/resource-videos/bulk/keywords/add', { ids: Array.from(selectedIds), keywords: bulkKwInput });
      await bulkFinish();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); setBulkBusy(false); }
  };

  const handleBulkRemoveKeywords = async () => {
    if (bulkRemoveKws.size === 0) return;
    setBulkBusy(true);
    try {
      await api.put('/resource-videos/bulk/keywords/remove', { ids: Array.from(selectedIds), keywords: Array.from(bulkRemoveKws) });
      await bulkFinish();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); setBulkBusy(false); }
  };

  const handleBulkAssign = async (patientCard) => {
    setBulkBusy(true);
    try {
      await api.put('/resource-videos/bulk/assign', { ids: Array.from(selectedIds), patientCard, label: assignLabel, durationDays: assignDuration });
      await bulkFinish();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); setBulkBusy(false); }
  };

  const handleBulkUnassign = async (patientCard) => {
    setBulkBusy(true);
    try {
      await api.put('/resource-videos/bulk/unassign', { ids: Array.from(selectedIds), patientCard });
      await bulkFinish();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); setBulkBusy(false); }
  };

  const handleBulkDelete = async () => {
    setBulkBusy(true);
    try {
      await api.post('/resource-videos/bulk/delete', { ids: Array.from(selectedIds) });
      await bulkFinish();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); setBulkBusy(false); }
  };

  const openBulkModal = (type) => {
    setBulkModal(type);
    setBulkKwInput('');
    setBulkRemoveKws(new Set());
    setPatientQuery(''); setPatientResults([]);
  };

  // ── PLAYLIST ACTIONS ──
  const handleAddPlaylist = async () => {
    const url = playlistUrl.trim();
    if (!url) return;
    setAddingPlaylist(true);
    setPlaylistImportMsg('Importing playlist...');
    try {
      const res = await api.post('/youtube/playlists', { playlistUrl: url });
      setPlaylistUrl('');
      setPlaylistImportMsg(`Imported ${res.data.importedCount} videos from "${res.data.playlist.title}"`);
      await fetchPlaylists();
      await fetchSavedVideos();
      setTimeout(() => setPlaylistImportMsg(''), 5000);
    } catch (err) {
      setPlaylistImportMsg('');
      alert(err.response?.data?.error || 'Failed to import playlist');
    } finally { setAddingPlaylist(false); }
  };

  const handleSyncPlaylist = async (pl) => {
    try {
      const res = await api.put(`/youtube/playlists/${pl._id}/sync`);
      const d = res.data || {};
      alert(`Synced: ${d.synced || 0} videos found, ${d.removed || 0} removed.`);
      await fetchPlaylists();
    } catch (err) { alert(err.response?.data?.error || 'Sync failed'); }
  };

  const handleDeletePlaylist = async (pl) => {
    if (!window.confirm(`Remove playlist "${pl.title}"? (Videos stay in library)`)) return;
    try {
      await api.delete(`/youtube/playlists/${pl._id}`);
      if (selectedPlId === pl.playlistId) setSelectedPlId(null);
      await fetchPlaylists();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handlePlaylistPlay = async (pl) => {
    try {
      const res = await api.get(`/youtube/playlist-videos/${pl.playlistId}`);
      const vids = res.data || [];
      if (vids.length === 0) { alert('No videos in this playlist.'); return; }
      setPlaylistQueue(vids);
      setPlaylistIdx(0);
      setPlayingVideo(vids[0]);
    } catch { alert('Failed to load playlist videos.'); }
  };

  const playlistPrev = () => {
    if (playlistIdx > 0) {
      const i = playlistIdx - 1;
      setPlaylistIdx(i);
      setPlayingVideo(playlistQueue[i]);
    }
  };

  const playlistNext = () => {
    if (playlistIdx < playlistQueue.length - 1) {
      const i = playlistIdx + 1;
      setPlaylistIdx(i);
      setPlayingVideo(playlistQueue[i]);
    }
  };

  // ── FOLDER ACTIONS ──
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      await api.post('/resource-files/folders', { name, parentPath: currentFolderPath });
      setNewFolderName('');
      setShowNewFolderInput(false);
      await fetchFolders(currentFolderPath);
    } catch (err) { alert('Failed to create folder: ' + (err.response?.data?.error || err.message)); }
    finally { setCreatingFolder(false); }
  };

  const handleRenameFolder = async () => {
    if (!renamingFolder || !renameFolderName.trim()) return;
    try {
      await api.put('/resource-files/folders/rename', { folderPath: renamingFolder.path, newName: renameFolderName.trim() });
      setRenamingFolder(null);
      setRenameFolderName('');
      await fetchFolders(currentFolderPath);
    } catch (err) { alert('Rename failed: ' + (err.response?.data?.error || err.message)); }
  };

  const handleDeleteFolder = async (folder, mode) => {
    const label = mode === 'delete_all' ? 'DELETE folder and ALL files inside' : 'delete folder and move files to root';
    if (!window.confirm(`${label} "${folder.name}"?`)) return;
    try {
      await api.delete('/resource-files/folders', { params: { path: folder.path, mode } });
      await fetchFolders(currentFolderPath);
      await fetchResFiles(currentFolderPath);
    } catch (err) { alert('Delete failed: ' + (err.response?.data?.error || err.message)); }
  };

  const handleMoveFiles = async () => {
    if (selectedFileIds.size === 0) return;
    try {
      await api.put('/resource-files/bulk/move', { ids: Array.from(selectedFileIds), folderPath: moveTargetPath });
      setSelectedFileIds(new Set());
      setShowMoveModal(false);
      await fetchResFiles(currentFolderPath);
    } catch (err) { alert('Move failed: ' + (err.response?.data?.error || err.message)); }
  };

  // ── FILE ACTIONS ──
  const handleUploadDoc = async (mode) => {
    if (!uploadDocFile) return;
    // If no mode specified, check for duplicate first
    if (!mode) {
      try {
        const check = await api.get('/resource-files/check-duplicate', { params: { filename: uploadDocFile.name } });
        if (check.data.exists) {
          setDupCheckResult({ exists: true, file: check.data.file, action: null });
          return; // wait for user decision
        }
      } catch { /* proceed with upload */ }
    }
    setDupCheckResult(null);
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadDocFile);
      if (uploadDocTitle.trim()) fd.append('title', uploadDocTitle.trim());
      if (uploadDocKw.trim()) fd.append('keywords', uploadDocKw.trim());
      if (mode) fd.append('mode', mode);
      fd.append('folderPath', currentFolderPath);
      await api.post('/resource-files/upload', fd, { timeout: 300000, headers: { 'Content-Type': undefined } });
      setUploadDocFile(null); setUploadDocTitle(''); setUploadDocKw('');
      if (docFileRef.current) docFileRef.current.value = '';
      setShowUploadDocModal(false);
      await fetchResFiles(currentFolderPath);
    } catch (err) {
      if (err.response?.data?.error === 'GDRIVE_REAUTH') {
        alert('Google Drive is disconnected. Please ask an admin/owner to reconnect it from the Resources page.');
      } else {
        alert('Upload failed: ' + (err.response?.data?.error || err.message));
      }
    }
    finally { setUploadingDoc(false); }
  };

  const handleDeleteFile = async (f) => {
    try {
      await api.delete(`/resource-files/${f._id}`);
      setResFiles(prev => prev.filter(x => x._id !== f._id));
      setFileActionItem(null);
    } catch { alert('Delete failed'); }
  };

  const handleFileAssign = async (fileId, patientCard) => {
    try {
      const res = await api.put(`/resource-files/${fileId}/assign`, { patientCard, durationDays: assignDuration });
      setResFiles(prev => prev.map(f => f._id === fileId ? res.data : f));
    } catch {}
  };

  const handleFileUnassign = async (fileId, patientCard) => {
    try {
      const res = await api.put(`/resource-files/${fileId}/unassign`, { patientCard });
      setResFiles(prev => prev.map(f => f._id === fileId ? res.data : f));
    } catch {}
  };

  const handleFileExtend = async (fileId, patientCard, days) => {
    try {
      const res = await api.put(`/resource-files/${fileId}/extend`, { patientCard, durationDays: days });
      setResFiles(prev => prev.map(f => f._id === fileId ? res.data : f));
    } catch {}
  };

  const handleBulkDeleteFiles = async () => {
    if (!window.confirm(`Delete ${selectedFileIds.size} file(s)? This cannot be undone.`)) return;
    try {
      await api.post('/resource-files/bulk/delete', { ids: Array.from(selectedFileIds) });
      await fetchResFiles(currentFolderPath);
      setSelectedFileIds(new Set());
    } catch { alert('Failed'); }
  };

  // ── PLAYLIST ASSIGN ──
  const [plAssignModal, setPlAssignModal] = useState(null); // playlist obj for assign modal

  const handlePlaylistAssign = async (plId, patientCard) => {
    try {
      await api.put(`/youtube/playlists/${plId}/assign`, { patientCard, label: assignLabel, durationDays: assignDuration });
      await fetchPlaylists();
      setPlAssignModal(null);
      setPatientQuery(''); setPatientResults([]);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handlePlaylistUnassign = async (plId, patientCard) => {
    try {
      await api.put(`/youtube/playlists/${plId}/unassign`, { patientCard });
      await fetchPlaylists();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handlePlaylistExtend = async (plId, patientCard, days) => {
    try {
      await api.put(`/youtube/playlists/${plId}/extend`, { patientCard, durationDays: days });
      await fetchPlaylists();
    } catch {}
  };

  const openFileInBrowser = (f) => {
    api.get(`/resource-files/stream/${f._id}`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(new Blob([r.data], { type: f.mimeType }));
        window.open(url, '_blank');
      })
      .catch(() => { alert('Could not open file.'); });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STYLES
  // ═══════════════════════════════════════════════════════════════════════════

  const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 13, flex: 1, minWidth: 0, outline: 'none' };
  const btnPrimary = { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
  const btnOutline = { padding: '6px 14px', borderRadius: 7, border: '1.5px solid #d1d5db', background: '#f9fafb', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  const kwInputStyle = { padding: '5px 10px', borderRadius: 6, border: '1.5px solid #fbbf24', fontSize: 11, outline: 'none', width: '100%', background: '#fffbeb' };

  const checkboxStyle = (checked) => ({
    width: 22, height: 22, borderRadius: 6, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.12s',
    background: checked ? '#4f46e5' : 'rgba(255,255,255,.85)',
    border: checked ? '2px solid #4f46e5' : '2px solid rgba(0,0,0,.25)',
    color: '#fff', fontSize: 13, fontWeight: 800,
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', padding: '12px 10px', maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Top Bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Resources</h2>
      </div>


      {/* ── Main Tabs: Videos & Shorts | Files & Docs ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 18, background: '#f1f5f9', borderRadius: 12, padding: 4, border: '1.5px solid #e2e8f0' }}>
        {[
          { key: 'videos', label: 'Videos & Shorts', icon: '📹', count: allVideos.length },
          { key: 'files',  label: 'Files & Docs', icon: '📄', count: resFiles.length },
        ].map(t => {
          const active = mainTab === t.key;
          return (
            <button key={t.key} onClick={() => { setMainTab(t.key); setSelectedIds(new Set()); setSelectedFileIds(new Set()); }}
              style={{
                flex: 1, padding: '10px 20px', fontSize: 14, fontWeight: active ? 700 : 600, cursor: 'pointer',
                background: active ? '#fff' : 'transparent',
                border: active ? '1.5px solid #e2e8f0' : '1.5px solid transparent',
                borderRadius: 9,
                color: active ? '#0f172a' : '#64748b',
                transition: 'all 0.2s',
                boxShadow: active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              {t.label} <span style={{ fontSize: 11, color: active ? '#4f46e5' : '#94a3b8', fontWeight: 700, marginLeft: 2, background: active ? '#eef2ff' : '#e2e8f0', borderRadius: 10, padding: '1px 7px' }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
           VIDEOS & SHORTS TAB
         ═══════════════════════════════════════════════════════════════════════ */}
      {mainTab === 'videos' && <>

      {/* ── Channel Filters (boxed) ── */}
      {channels.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center',
          padding: '8px 12px', background: '#f8fafc', borderRadius: 10, border: '1.5px solid #e2e8f0',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>Channels</span>
          <button onClick={() => setSelectedChIds(new Set(channels.map(c => c.channelId)))}
            style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: selectedChIds.size === channels.length ? '#0f172a' : '#fff',
              color: selectedChIds.size === channels.length ? '#fff' : '#64748b',
              border: selectedChIds.size === channels.length ? '1.5px solid #0f172a' : '1.5px solid #cbd5e1',
              transition: 'all 0.15s',
            }}>All</button>
          {channels.map(ch => {
            const sel = selectedChIds.has(ch.channelId);
            return (
              <div key={ch._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                <button onClick={() => toggleChannelSelection(ch.channelId)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                    background: sel ? '#4f46e5' : '#fff',
                    color: sel ? '#fff' : '#475569',
                    border: sel ? '1.5px solid #4f46e5' : '1.5px solid #cbd5e1',
                    transition: 'all 0.15s',
                  }}>{ch.name || ch.channelId}</button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Filters + Search + Add (single line) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
        {[
          { key: 'all',       label: 'All',       count: allVideos.length, active: isAllSelected, color: '#0f172a', onClick: selectAll },
          { key: 'video',     label: 'Videos',    count: videoCount,       active: viewMode === 'videos' && activeFilters.has('video') && !activeFilters.has('short'), color: '#4f46e5', onClick: () => { toggleFilter('video'); setViewMode('videos'); setSelectedPlId(null); } },
          { key: 'short',     label: 'Shorts',    count: shortCount,       active: viewMode === 'videos' && activeFilters.has('short') && !activeFilters.has('video'), color: '#ec4899', onClick: () => { toggleFilter('short'); setViewMode('videos'); setSelectedPlId(null); } },
          { key: 'playlists', label: 'Playlists', count: playlistCount,    active: viewMode === 'playlists', color: '#7c3aed', onClick: () => { setViewMode('playlists'); setSelectedPlId(null); setSelectedIds(new Set()); } },
        ].map(t => (
          <button key={t.key} onClick={t.onClick}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              background: t.active ? t.color : 'transparent',
              color: t.active ? '#fff' : '#475569',
              border: t.active ? `1.5px solid ${t.color}` : '1.5px solid #cbd5e1',
              transition: 'all 0.15s', whiteSpace: 'nowrap',
            }}>{t.label} ({t.count})</button>
        ))}
        <div style={{ flex: 1, minWidth: 0 }}>
          <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setSelectedIds(new Set()); }}
            placeholder={viewMode === 'playlists' ? 'Search playlists...' : 'Search...'}
            style={{ ...inputStyle, width: '100%', borderRadius: 20, padding: '6px 14px', fontSize: 12 }} />
        </div>
        <button onClick={() => setShowAddModal(true)}
          style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12, borderRadius: 20, background: '#4f46e5', whiteSpace: 'nowrap' }}>
          + Add
        </button>
      </div>

      {/* ═══════ PLAYLISTS GRID VIEW ═══════ */}
      {viewMode === 'playlists' && (() => {
        const q2 = searchQuery.toLowerCase();
        const fPl = q2 ? playlists.filter(pl => (pl.title || '').toLowerCase().includes(q2)) : playlists;
        return (
          <div style={{ marginBottom: 32 }}>
            {fPl.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>
                {playlists.length === 0 ? 'No playlists imported yet. Use "+ Add Channel / Video" and select the Playlist tab.' : 'No playlists match your search.'}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
                {fPl.map(pl => (
                  <div key={pl._id} style={{
                    border: '1.5px solid #c4b5fd', borderRadius: 12, overflow: 'hidden', background: '#fff',
                    boxShadow: '0 2px 8px rgba(124,58,237,.08)', cursor: 'pointer', transition: 'all 0.15s',
                  }}
                    onClick={() => { setSelectedPlId(pl.playlistId); setViewMode('videos'); }}
                  >
                    {/* Thumbnail */}
                    <div style={{ position: 'relative', background: '#f5f3ff', height: 170, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {pl.thumbnail ? (
                        <img src={pl.thumbnail} alt={pl.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ fontSize: 48, color: '#c4b5fd' }}>{'\u25b6'}</div>
                      )}
                      {/* Video count badge */}
                      <div style={{
                        position: 'absolute', bottom: 8, right: 8,
                        background: 'rgba(0,0,0,.75)', color: '#fff', borderRadius: 6,
                        padding: '3px 8px', fontSize: 11, fontWeight: 700,
                      }}>{pl.videoCount} videos</div>
                      {/* Play overlay */}
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(0,0,0,.12)', opacity: 0, transition: 'opacity 0.15s',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '0'; }}
                      >
                        <div style={{
                          width: 52, height: 52, borderRadius: '50%', background: 'rgba(124,58,237,.85)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 24,
                        }}>{'\u25b6'}</div>
                      </div>
                    </div>
                    {/* Body */}
                    <div style={{ padding: '10px 14px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e1b4b', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pl.title || pl.playlistId}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>
                          {(pl.assignedPatients || []).length > 0 ? `Assigned to ${pl.assignedPatients.length} patient${pl.assignedPatients.length > 1 ? 's' : ''}` : 'Not assigned'}
                        </span>
                        <button onClick={e => { e.stopPropagation(); handleDeletePlaylist(pl); }}
                          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '2px 6px' }}>
                          Delete
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button onClick={e => { e.stopPropagation(); handlePlaylistPlay(pl); }}
                          style={{ ...btnPrimary, padding: '5px 12px', fontSize: 11, background: '#7c3aed' }}>
                          Play All
                        </button>
                        <button onClick={e => { e.stopPropagation(); handleSyncPlaylist(pl); }}
                          style={{ ...btnOutline, padding: '5px 12px', fontSize: 11, color: '#2563eb', borderColor: '#93c5fd' }}>
                          Sync
                        </button>
                        <button onClick={e => { e.stopPropagation(); setPlAssignModal(pl); setPatientQuery(''); setPatientResults([]); }}
                          style={{ ...btnOutline, padding: '5px 12px', fontSize: 11, color: '#059669', borderColor: '#6ee7b7' }}>
                          Assign
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══════ PLAYLIST DRILL-DOWN HEADER ═══════ */}
      {viewMode === 'videos' && selectedPlId && (() => {
        const drillPl = playlists.find(p => p.playlistId === selectedPlId);
        if (!drillPl) return null;
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap',
            padding: '10px 14px', background: '#f5f3ff', borderRadius: 10, border: '1.5px solid #c4b5fd',
          }}>
            <button onClick={() => { setViewMode('playlists'); setSelectedPlId(null); }}
              style={{ ...btnOutline, padding: '6px 12px', fontSize: 12, fontWeight: 700, color: '#7c3aed', borderColor: '#c4b5fd' }}>
              {'\u2190'} Playlists
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1e1b4b' }}>{drillPl.title}</span>
              <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, marginLeft: 8 }}>({drillPl.videoCount} videos)</span>
            </div>
            <button onClick={() => handlePlaylistPlay(drillPl)}
              style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12, background: '#7c3aed' }}>
              Play All
            </button>
            <button onClick={() => { setPlAssignModal(drillPl); setPatientQuery(''); setPatientResults([]); }}
              style={{ ...btnOutline, padding: '6px 14px', fontSize: 12, color: '#059669', borderColor: '#6ee7b7' }}>
              Assign Playlist
            </button>
          </div>
        );
      })()}

      {/* ── BULK ACTION TOOLBAR (appears when 2+ selected) ── */}
      {viewMode === 'videos' && selectedIds.size >= 2 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', marginBottom: 14,
          background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
          borderRadius: 12, border: '1.5px solid #a5b4fc',
          boxShadow: '0 2px 8px rgba(79,70,229,.12)',
        }}>
          {/* Select All checkbox */}
          <div onClick={toggleSelectAll} style={checkboxStyle(allVisibleSelected)} title="Select all visible">
            {allVisibleSelected ? '\u2713' : ''}
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#312e81', marginRight: 4 }}>
            {selectedIds.size} selected
          </span>
          <div style={{ width: 1, height: 24, background: '#a5b4fc', margin: '0 4px' }} />

          {/* Bulk action buttons */}
          <button onClick={() => openBulkModal('addKeywords')} style={{
            padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: '#fff', color: '#4f46e5', border: '1.5px solid #a5b4fc',
          }}>+ Keywords</button>
          <button onClick={() => openBulkModal('removeKeywords')} style={{
            padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: '#fff', color: '#b45309', border: '1.5px solid #fbbf24',
          }}>- Keywords</button>
          <button onClick={() => openBulkModal('assign')} style={{
            padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: '#fff', color: '#059669', border: '1.5px solid #6ee7b7',
          }}>Assign</button>
          <button onClick={() => openBulkModal('unassign')} style={{
            padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: '#fff', color: '#d97706', border: '1.5px solid #fcd34d',
          }}>Unassign</button>
          <button onClick={() => openBulkModal('delete')} style={{
            padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fca5a5',
          }}>Delete</button>

          <div style={{ flex: 1 }} />
          <button onClick={() => setSelectedIds(new Set())} style={{
            background: 'none', border: 'none', color: '#6366f1', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', textDecoration: 'underline',
          }}>Clear</button>
        </div>
      )}

      {/* ── Inline Video Player ── */}
      {viewMode === 'videos' && playingVideo && (
        <div style={{
          marginBottom: 16, borderRadius: 12, overflow: 'hidden',
          border: '2px solid #4f46e5', background: '#000', position: 'relative',
        }}>
          <button onClick={() => setPlayingVideo(null)}
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 10,
              background: 'rgba(0,0,0,.7)', color: '#fff', border: 'none',
              borderRadius: '50%', width: 32, height: 32, fontSize: 16,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>x</button>
          <div style={{ padding: '8px 12px', background: '#1e1b4b', color: '#fff', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {playingVideo.title || 'Playing video'}
            </span>
            {playlistQueue.length > 1 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <button onClick={playlistPrev} disabled={playlistIdx <= 0}
                  style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14, opacity: playlistIdx <= 0 ? .3 : 1 }}>{'\u25C0'}</button>
                <span style={{ fontSize: 11, color: '#a5b4fc' }}>{playlistIdx + 1}/{playlistQueue.length}</span>
                <button onClick={playlistNext} disabled={playlistIdx >= playlistQueue.length - 1}
                  style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14, opacity: playlistIdx >= playlistQueue.length - 1 ? .3 : 1 }}>{'\u25B6'}</button>
              </span>
            )}
          </div>
          {playingVideo.streamUrl ? (
            <video
              src={playingVideo.streamUrl} controls controlsList="nodownload"
              onContextMenu={e => e.preventDefault()} autoPlay playsInline
              style={{ width: '100%', maxHeight: 480, objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <iframe
              src={`https://www.youtube.com/embed/${playingVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={playingVideo.title || 'Video'}
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
              style={{ width: '100%', height: 480, border: 'none', display: 'block' }}
            />
          )}
        </div>
      )}

      {/* ── Video Grid ── */}
      {viewMode === 'videos' && <div style={{ marginBottom: 32 }}>
        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>Loading resources...</div>
        ) : filteredVideos.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>
            {allVideos.length === 0
              ? 'No videos found. Click "+ Add Channel / Video" to get started.'
              : 'No results found' + (searchQuery ? ' matching your search' : '') + '.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {filteredVideos.map(v => {
              const isSaved = v.isSaved;
              const isDrive = v.source === 'drive';
              const assignCards = isSaved ? getAssignedCards(v.assignedPatients) : [];
              const channelName = channels.find(c => c.channelId === v.channelId)?.name;
              const cardKey = v._id || v.youtubeId || v.driveFileId;
              const isShort = v.videoType === 'short';
              const thumbH = isShort ? 260 : 195;
              const isChecked = isSaved && v._id && selectedIds.has(v._id);

              return (
                <div key={cardKey} style={{
                  border: isChecked ? '2px solid #4f46e5' : (isSaved ? '1.5px solid #c7d2fe' : '1.5px solid #e5e7eb'),
                  borderRadius: 12, overflow: 'hidden',
                  background: isChecked ? '#eef2ff' : '#fff',
                  boxShadow: isChecked ? '0 0 0 2px rgba(79,70,229,.15)' : '0 2px 8px rgba(0,0,0,.07)',
                  transition: 'all 0.15s',
                }}>
                  {/* Thumbnail */}
                  <div style={{ position: 'relative', cursor: 'pointer' }}
                    onClick={() => playVideo(v)}>
                    {v.thumbnail ? (
                      <img src={v.thumbnail} alt={v.title || 'Video'}
                        style={{ width: '100%', height: thumbH, objectFit: 'cover', display: 'block', background: '#f3f4f6' }}
                        onError={e => { if (v.youtubeId) { e.target.onerror = null; e.target.src = `https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`; } }}
                      />
                    ) : (
                      <div style={{
                        width: '100%', height: thumbH,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isDrive ? '#dbeafe' : '#f3f4f6', color: isDrive ? '#1d4ed8' : '#9ca3af', fontSize: 40,
                      }}>{isDrive ? '\u2601' : '\u25b6'}</div>
                    )}
                    {/* Play overlay */}
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,.15)', opacity: 0, transition: 'opacity 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '0'; }}
                    >
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', background: 'rgba(0,0,0,.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 22,
                      }}>{'\u25b6'}</div>
                    </div>
                    {/* Badges */}
                    <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4 }}>
                      {isSaved && <span style={{ background: '#4f46e5', color: '#fff', borderRadius: 6, padding: '2px 7px', fontSize: 9, fontWeight: 700 }}>Saved</span>}
                      {isDrive && <span style={{ background: '#1d4ed8', color: '#fff', borderRadius: 6, padding: '2px 7px', fontSize: 9, fontWeight: 700 }}>Drive</span>}
                      {isShort && <span style={{ background: '#db2777', color: '#fff', borderRadius: 6, padding: '2px 7px', fontSize: 9, fontWeight: 700 }}>Short</span>}
                    </div>
                    {/* Checkbox (only for saved videos) */}
                    {isSaved && v._id && (
                      <div onClick={e => { e.stopPropagation(); toggleSelect(v._id); }}
                        style={{ ...checkboxStyle(isChecked), position: 'absolute', top: 6, right: 6 }}
                        title="Select for bulk action"
                      >{isChecked ? '\u2713' : ''}</div>
                    )}
                    {v.duration > 0 && (
                      <span style={{
                        position: 'absolute', bottom: 6, right: 6,
                        background: 'rgba(0,0,0,.75)', color: '#fff', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 600,
                      }}>{v.duration >= 3600 ? `${Math.floor(v.duration/3600)}:${String(Math.floor((v.duration%3600)/60)).padStart(2,'0')}:${String(v.duration%60).padStart(2,'0')}` : `${Math.floor(v.duration/60)}:${String(v.duration%60).padStart(2,'0')}`}</span>
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.4, marginBottom: 3,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>{v.title || v.youtubeId || 'Untitled'}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          {channelName && <span style={{ color: '#6366f1', fontWeight: 600 }}>{channelName} · </span>}
                          {fmtDate(v.publishedAt)}
                          {assignCards.length > 0 && <span style={{ color: '#4f46e5', fontWeight: 600 }}> · {assignCards.length} assigned</span>}
                        </div>
                      </div>
                      {/* Kebab menu */}
                      <button onClick={e => { e.stopPropagation(); openActionModal(v); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
                          fontSize: 18, color: '#6b7280', lineHeight: 1, flexShrink: 0,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                          borderRadius: 6, transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                        title="Actions"
                      >
                        <span style={{ display: 'flex', gap: 3 }}>
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280' }} />
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280' }} />
                        </span>
                        <span style={{ display: 'flex', gap: 3 }}>
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280' }} />
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280' }} />
                        </span>
                        <span style={{ display: 'flex', gap: 3 }}>
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280' }} />
                          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#6b7280' }} />
                        </span>
                      </button>
                    </div>
                    {(v.keywords || []).length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                        {v.keywords.slice(0, 4).map(kw => (
                          <span key={kw} style={{ background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>{kw}</span>
                        ))}
                        {v.keywords.length > 4 && <span style={{ color: '#9ca3af', fontSize: 10 }}>+{v.keywords.length - 4}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>}

      </>}

      {/* ═══════════════════════════════════════════════════════════════════════
           FILES & DOCS TAB
         ═══════════════════════════════════════════════════════════════════════ */}
      {mainTab === 'files' && (() => {
        const iconColors = { pdf: '#ef4444', document: '#3b82f6', spreadsheet: '#22c55e', presentation: '#f59e0b', image: '#a855f7', link: '#0891b2', other: '#6b7280' };
        const iconLabels = { pdf: 'PDF', document: 'DOC', spreadsheet: 'XLS', presentation: 'PPT', image: 'IMG', link: 'LINK', other: 'FILE' };
        const categories = ['all', 'pdf', 'document', 'spreadsheet', 'image', 'link', 'other'];
        const catLabels = { all: 'All', pdf: 'PDFs', document: 'Documents', spreadsheet: 'Spreadsheets', image: 'Images', link: 'Links', other: 'Other' };

        const q3 = fileSearchQuery.toLowerCase();
        const isSearching = !!q3;
        const catFiltered = fileCategory === 'all' ? resFiles : resFiles.filter(f => f.category === fileCategory);
        const fFiles = q3 ? catFiltered.filter(f =>
          (f.title || '').toLowerCase().includes(q3) ||
          (f.filename || '').toLowerCase().includes(q3) ||
          (f.keywords || []).some(kw => kw.toLowerCase().includes(q3)) ||
          (f.uploadedBy || '').toLowerCase().includes(q3)
        ) : catFiltered;
        const formatSize = (b) => {
          if (!b) return '';
          if (b < 1024) return b + ' B';
          if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
          return (b / 1048576).toFixed(1) + ' MB';
        };

        // ── Sort logic ──
        const toggleSort = (col) => {
          if (fileSortCol === col) setFileSortDir(d => d === 'asc' ? 'desc' : 'asc');
          else { setFileSortCol(col); setFileSortDir(col === 'name' ? 'asc' : 'desc'); }
        };
        const sortArrow = (col) => fileSortCol === col ? (fileSortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';
        const sortedFiles = [...fFiles].sort((a, b) => {
          const dir = fileSortDir === 'asc' ? 1 : -1;
          switch (fileSortCol) {
            case 'name': return dir * (a.title || a.filename || '').localeCompare(b.title || b.filename || '');
            case 'type': { const ae = (a.filename || '').split('.').pop(); const be = (b.filename || '').split('.').pop(); return dir * ae.localeCompare(be); }
            case 'size': return dir * ((a.size || 0) - (b.size || 0));
            case 'date': return dir * (new Date(a.uploadedAt || 0) - new Date(b.uploadedAt || 0));
            case 'by': return dir * (a.uploadedBy || '').localeCompare(b.uploadedBy || '');
            default: return 0;
          }
        });
        const thStyle = (col) => ({
          cursor: 'pointer', userSelect: 'none', color: fileSortCol === col ? '#1e293b' : '#6b7280',
          fontWeight: fileSortCol === col ? 800 : 700, whiteSpace: 'nowrap',
        });
        const gridCols = isMobile
          ? '32px 1fr 32px'
          : canSeeEncryption ? '32px 1fr 54px 64px 100px 74px 36px 32px' : '32px 1fr 54px 64px 100px 74px 32px';

        return (
          <div>
            {/* Toolbar: search + action buttons */}
            <div style={{ marginBottom: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '0 1 280px', position: 'relative', minWidth: 160 }}>
                <input value={fileSearchQuery} onChange={e => setFileSearchQuery(e.target.value)}
                  placeholder="Search files..."
                  style={{ ...inputStyle, width: '100%', paddingLeft: 30, paddingRight: fileSearchQuery ? 28 : 12, fontSize: 12 }} />
                <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
                {fileSearchQuery && (
                  <button onClick={() => setFileSearchQuery('')}
                    style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 14, color: '#94a3b8', cursor: 'pointer', padding: '2px 4px' }}>✕</button>
                )}
              </div>
              <button onClick={() => setShowNewFolderInput(v => !v)}
                style={{ ...btnPrimary, padding: '8px 14px', fontSize: 12, borderRadius: 10, background: '#d97706', whiteSpace: 'nowrap' }}>
                + Folder
              </button>
              <button onClick={() => setShowAddLinkModal(true)}
                style={{ ...btnPrimary, padding: '8px 14px', fontSize: 12, borderRadius: 10, background: '#0891b2', whiteSpace: 'nowrap' }}>
                + Link
              </button>
              <button onClick={() => setShowUploadDocModal(true)}
                style={{ ...btnPrimary, padding: '8px 14px', fontSize: 12, borderRadius: 10, background: '#4f46e5', whiteSpace: 'nowrap' }}>
                + File
              </button>
            </div>

            {/* New folder input */}
            {showNewFolderInput && !isSearching && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                <input autoFocus placeholder="Folder name" value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowNewFolderInput(false); setNewFolderName(''); } }}
                  style={{ flex: 1, padding: '7px 12px', borderRadius: 8, border: '1.5px solid #93c5fd', fontSize: 13, outline: 'none', maxWidth: 300 }} />
                <button onClick={handleCreateFolder} disabled={creatingFolder}
                  style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {creatingFolder ? '...' : 'Create'}
                </button>
                <button onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            )}

            {/* Breadcrumb navigation */}
            {!isSearching && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 10, fontSize: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, marginRight: 4 }}>{'\uD83D\uDCC2'}</span>
                <button onClick={() => setCurrentFolderPath('/')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: currentFolderPath === '/' ? '#0f172a' : '#4f46e5', fontWeight: 800, fontSize: 14, padding: '2px 4px' }}>
                  Root
                </button>
                {currentFolderPath !== '/' && currentFolderPath.replace(/^\//, '').split('/').map((seg, i, arr) => {
                  const segPath = '/' + arr.slice(0, i + 1).join('/');
                  const isLast = i === arr.length - 1;
                  return (
                    <React.Fragment key={segPath}>
                      <span style={{ color: '#475569', margin: '0 2px', fontWeight: 900, fontSize: 15 }}>/</span>
                      <button onClick={() => !isLast && setCurrentFolderPath(segPath)}
                        style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer',
                                 color: isLast ? '#0f172a' : '#4f46e5', fontWeight: isLast ? 800 : 700, fontSize: 14, padding: '2px 4px' }}>
                        {seg}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {/* Searching indicator */}
            {isSearching && (
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontStyle: 'italic' }}>
                Searching across all folders...
              </div>
            )}

            {/* Category filters */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button key={cat} onClick={() => setFileCategory(cat)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: fileCategory === cat ? '#4f46e5' : 'transparent',
                    color: fileCategory === cat ? '#fff' : '#475569',
                    border: fileCategory === cat ? '1.5px solid #4f46e5' : '1.5px solid #cbd5e1',
                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}>{catLabels[cat]} ({cat === 'all' ? resFiles.length : resFiles.filter(f => f.category === cat).length})</button>
              ))}
              {fileSearchQuery && (
                <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>{fFiles.length} result{fFiles.length !== 1 ? 's' : ''}</span>
              )}
            </div>

            {/* Bulk toolbar for files */}
            {selectedFileIds.size >= 2 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', marginBottom: 14,
                background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                borderRadius: 12, border: '1.5px solid #a5b4fc',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#312e81' }}>{selectedFileIds.size} selected</span>
                <button onClick={() => { fetchAllFolders(); setMoveTargetPath('/'); setShowMoveModal(true); }} style={{
                  padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: '#fefce8', color: '#92400e', border: '1.5px solid #fde68a',
                }}>Move to...</button>
                <button onClick={handleBulkDeleteFiles} style={{
                  padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fca5a5',
                }}>Delete</button>
                <div style={{ flex: 1 }} />
                <button onClick={() => setSelectedFileIds(new Set())} style={{
                  background: 'none', border: 'none', color: '#6366f1', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', textDecoration: 'underline',
                }}>Clear</button>
              </div>
            )}

            {/* File + Folder table */}
            {loadingFiles ? (
              <div style={{ color: '#9ca3af', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>Loading files...</div>
            ) : (sortedFiles.length === 0 && (!isSearching ? folders.length === 0 : true)) ? (
              <div style={{ color: '#9ca3af', fontSize: 14, padding: '30px 0', textAlign: 'center' }}>
                {resFiles.length === 0 ? 'No files uploaded yet. Use the buttons above.' : 'No files match this filter.'}
              </div>
            ) : (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                {/* Table header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: gridCols,
                  gap: 4, padding: '8px 10px', fontSize: 10, color: '#6b7280',
                  textTransform: 'uppercase', letterSpacing: '.5px',
                  background: '#f1f5f9', borderBottom: '2px solid #e2e8f0',
                  alignItems: 'center',
                }}>
                  <span></span>
                  <span style={thStyle('name')} onClick={() => toggleSort('name')}>Name{sortArrow('name')}</span>
                  {!isMobile && <span style={thStyle('type')} onClick={() => toggleSort('type')}>Type{sortArrow('type')}</span>}
                  {!isMobile && <span style={thStyle('size')} onClick={() => toggleSort('size')}>Size{sortArrow('size')}</span>}
                  {!isMobile && <span style={thStyle('date')} onClick={() => toggleSort('date')}>Date{sortArrow('date')}</span>}
                  {!isMobile && <span style={thStyle('by')} onClick={() => toggleSort('by')}>By{sortArrow('by')}</span>}
                  {!isMobile && canSeeEncryption && <span style={{ fontWeight: 700 }}>Enc</span>}
                  <span></span>
                </div>

                {/* Folder rows (always on top) */}
                {!isSearching && folders.map(folder => (
                  <div key={'folder-' + folder.path}
                    onClick={() => { setCurrentFolderPath(folder.path); setSelectedFileIds(new Set()); }}
                    style={{
                      display: 'grid', gridTemplateColumns: gridCols,
                      gap: 4, padding: '9px 10px', alignItems: 'center',
                      background: '#fefce8', borderBottom: '1px solid #fde68a',
                      cursor: 'pointer', transition: 'background 0.12s', position: 'relative',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef9c3'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fefce8'; }}
                  >
                    {/* Folder icon */}
                    <div style={{ width: 30, height: 30, borderRadius: 7, background: '#fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                      {'\uD83D\uDCC1'}
                    </div>
                    {/* Folder name */}
                    <div style={{ minWidth: 0 }}>
                      {renamingFolder && renamingFolder.path === folder.path ? (
                        <input autoFocus value={renameFolderName}
                          onChange={e => setRenameFolderName(e.target.value)}
                          onBlur={handleRenameFolder}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(); if (e.key === 'Escape') setRenamingFolder(null); }}
                          onClick={e => e.stopPropagation()}
                          style={{ width: '80%', padding: '3px 8px', borderRadius: 6, border: '1.5px solid #93c5fd', fontSize: 13, outline: 'none', fontWeight: 600 }} />
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{folder.name}</span>
                      )}
                    </div>
                    {/* Folder label + empty cells (hidden on mobile) */}
                    {!isMobile && <span style={{ fontSize: 10, fontWeight: 700, color: '#a16207' }}>Folder</span>}
                    {!isMobile && <><span></span><span></span><span></span></>}
                    {!isMobile && canSeeEncryption && <span></span>}
                    {/* Folder actions */}
                    <button onClick={e => { e.stopPropagation(); setFolderActionMenu(folderActionMenu?.path === folder.path ? null : folder); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#a16207', padding: '2px 4px' }}>
                      ⋮
                    </button>
                    {/* Folder action dropdown */}
                    {folderActionMenu && folderActionMenu.path === folder.path && (
                      <div onClick={e => e.stopPropagation()} style={{
                        position: 'absolute', right: 8, top: 40, background: '#fff', border: '1px solid #e5e7eb',
                        borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 20, minWidth: 150, padding: '4px 0',
                      }}>
                        <div onClick={() => { setRenamingFolder(folder); setRenameFolderName(folder.name); setFolderActionMenu(null); }}
                          style={{ padding: '9px 14px', fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                          Rename
                        </div>
                        <div onClick={() => { handleDeleteFolder(folder, 'move_to_root'); setFolderActionMenu(null); }}
                          style={{ padding: '9px 14px', fontSize: 12, cursor: 'pointer', color: '#b45309' }}>
                          Delete (move files to root)
                        </div>
                        <div onClick={() => { handleDeleteFolder(folder, 'delete_all'); setFolderActionMenu(null); }}
                          style={{ padding: '9px 14px', fontSize: 12, cursor: 'pointer', color: '#dc2626' }}>
                          Delete (remove all files)
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* File rows */}
                {sortedFiles.map((f, i) => {
                  const cat = f.category || 'other';
                  const isChecked = selectedFileIds.has(f._id);
                  const fileExt = (f.filename || '').split('.').pop().toUpperCase();
                  return (
                    <div key={f._id} style={{
                      display: 'grid', gridTemplateColumns: gridCols,
                      gap: 4, padding: '8px 10px', alignItems: 'center',
                      background: isChecked ? '#eef2ff' : (i % 2 ? '#f8fafc' : '#fff'),
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                      onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = i % 2 ? '#f8fafc' : '#fff'; }}
                      onDoubleClick={() => f.isLink && f.linkUrl ? window.open(f.linkUrl, '_blank') : openFileInBrowser(f)}
                    >
                      {/* Checkbox / icon */}
                      <div onClick={e => { e.stopPropagation(); setSelectedFileIds(prev => { const n = new Set(prev); if (n.has(f._id)) n.delete(f._id); else n.add(f._id); return n; }); }}
                        style={{
                          width: 30, height: 30, borderRadius: 7,
                          background: isChecked ? '#4f46e5' : ((iconColors[cat] || '#6b7280') + '18'),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: isChecked ? 14 : 10,
                          color: isChecked ? '#fff' : (iconColors[cat] || '#6b7280'),
                        }}>
                        {isChecked ? '\u2713' : (iconLabels[cat] || 'FILE')}
                      </div>

                      {/* Name */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.title || f.filename}
                        </div>
                        {isMobile && (
                          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                            {f.isLink ? 'Link' : '.' + fileExt} {!f.isLink && f.size ? `· ${formatSize(f.size)}` : ''} {f.uploadedAt ? `· ${fmtDate(f.uploadedAt)}` : ''}
                          </div>
                        )}
                        {isSearching && f.folderPath && f.folderPath !== '/' && (
                          <div style={{ fontSize: 9, color: '#6b7280', marginTop: 1, cursor: 'pointer' }}
                            onClick={e => { e.stopPropagation(); setFileSearchQuery(''); setCurrentFolderPath(f.folderPath); }}>
                            {'\uD83D\uDCC1'} {f.folderPath}
                          </div>
                        )}
                        {(f.keywords || []).length > 0 && (
                          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 2 }}>
                            {f.keywords.slice(0, 3).map(kw => (
                              <span key={kw} style={{ background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '0px 5px', fontSize: 9, fontWeight: 600 }}>{kw}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* File type */}
                      {!isMobile && <span style={{ fontSize: 10, fontWeight: 700, color: iconColors[cat] || '#6b7280' }}>{f.isLink ? 'LINK' : '.' + fileExt}</span>}

                      {/* Size */}
                      {!isMobile && <span style={{ fontSize: 11, color: '#6b7280' }}>{f.isLink ? '\u2014' : formatSize(f.size)}</span>}

                      {/* Uploaded date */}
                      {!isMobile && <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: '#6b7280' }}>{fmtDate(f.uploadedAt)}</div>
                        {f.uploadedAt && <div style={{ fontSize: 9, color: '#b0b8c4' }}>{new Date(f.uploadedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>}
                      </div>}

                      {/* Uploader */}
                      {!isMobile && <span style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.uploadedBy || '-'}</span>}

                      {/* Encryption badge */}
                      {!isMobile && canSeeEncryption && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {f.encrypted ? (
                            <span style={{ color: '#059669', display: 'flex' }} title="AES-256-GCM encrypted">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            </span>
                          ) : (
                            <span style={{ color: '#d97706', fontSize: 13 }} title="Not encrypted">{'\u26A0'}</span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <button onClick={e => { e.stopPropagation(); setFileActionItem(f); setFileActionTab('info'); setPatientQuery(''); setPatientResults([]); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', fontSize: 16, color: '#6b7280', borderRadius: 6 }}
                        title="Actions">
                        ⋮
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ FILE ACTION MODAL ═══ */}
      {fileActionItem && (() => {
        const f = resFiles.find(x => x._id === fileActionItem._id) || fileActionItem;
        const fAssignCards = getAssignedCards(f.assignedPatients);

        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
          }} onClick={e => { if (e.target === e.currentTarget) setFileActionItem(null); }}>
            <div style={{
              background: '#fff', borderRadius: 20, maxWidth: 500, width: '94%', maxHeight: '85vh',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Header */}
              <div style={{
                padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: ((({ pdf: '#ef4444', document: '#3b82f6', spreadsheet: '#22c55e', image: '#a855f7' })[f.category] || '#6b7280') + '18'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: 10,
                  color: ({ pdf: '#ef4444', document: '#3b82f6', spreadsheet: '#22c55e', image: '#a855f7' })[f.category] || '#6b7280',
                }}>
                  {({ pdf: 'PDF', document: 'DOC', spreadsheet: 'XLS', image: 'IMG' })[f.category] || 'FILE'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.title || f.filename}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{f.filename} {f.size ? `(${(f.size / 1048576).toFixed(1)} MB)` : ''}</div>
                </div>
                <button onClick={() => setFileActionItem(null)} style={{
                  background: '#f3f4f6', border: 'none', width: 32, height: 32, borderRadius: '50%',
                  fontSize: 16, cursor: 'pointer', color: '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>x</button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 8px', background: '#fafafa' }}>
                {[
                  { key: 'info', label: 'Overview' }, { key: 'assign', label: 'Assign' },
                  { key: 'keywords', label: 'Keywords' }, { key: 'delete', label: 'Delete' },
                ].map(t => (
                  <button key={t.key} onClick={() => setFileActionTab(t.key)} style={{
                    padding: '10px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: 'none', border: 'none',
                    color: fileActionTab === t.key ? (t.key === 'delete' ? '#dc2626' : '#4f46e5') : '#6b7280',
                    borderBottom: fileActionTab === t.key ? `2.5px solid ${t.key === 'delete' ? '#dc2626' : '#4f46e5'}` : '2.5px solid transparent',
                  }}>{t.label}</button>
                ))}
              </div>

              {/* Content */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                {fileActionTab === 'info' && (
                  <div>
                    {/* File type + dates */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14, padding: '10px 12px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>File Type</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 2 }}>{(f.filename || '').split('.').pop().toUpperCase()} ({f.category || 'other'})</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Uploaded</div>
                        <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>{f.uploadedAt ? new Date(f.uploadedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                      </div>
                      {f.createdAt && (
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Created</div>
                          <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>{new Date(f.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      )}
                      {f.updatedAt && f.updatedAt !== f.createdAt && (
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Updated</div>
                          <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>{new Date(f.updatedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Location</div>
                      <div style={{ fontSize: 12, color: '#4f46e5', cursor: 'pointer' }}
                        onClick={() => { setCurrentFolderPath(f.folderPath || '/'); setFileActionItem(null); }}>
                        {'\uD83D\uDCC1'} {f.folderPath === '/' || !f.folderPath ? 'Root' : f.folderPath}
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Keywords</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(f.keywords || []).length > 0 ? f.keywords.map(kw => (
                          <span key={kw} style={{ background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{kw}</span>
                        )) : <span style={{ color: '#9ca3af', fontSize: 12 }}>None</span>}
                      </div>
                    </div>
                    {canSeeEncryption && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Encryption</div>
                        <span style={{ fontSize: 12, color: f.encrypted ? '#059669' : '#9ca3af' }}>
                          {f.encrypted ? 'AES-256-GCM encrypted on Drive' : 'Not encrypted'}
                        </span>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Assigned Patients</div>
                      {fAssignCards.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {fAssignCards.map(card => {
                            var ae = getAssignmentEntry(f.assignedPatients, card);
                            var expLabel = ae ? fmtExpiry(ae.validUntil) : null;
                            return (
                            <div key={card} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                              <div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>{card}</span>
                                {expLabel && <span style={{ fontSize: 10, color: expLabel === 'Expired' ? '#dc2626' : '#6b7280', marginLeft: 8 }}>{expLabel}</span>}
                              </div>
                              <select onChange={e => { if (e.target.value) handleFileExtend(f._id, card, parseInt(e.target.value)); e.target.value = ''; }}
                                defaultValue="" style={{ padding: '3px 6px', borderRadius: 6, fontSize: 10, border: '1px solid #a5b4fc', color: '#4f46e5', cursor: 'pointer', background: '#eef2ff' }}>
                                <option value="" disabled>Extend</option>
                                {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                              </select>
                            </div>
                            );
                          })}
                        </div>
                      ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>Not assigned</span>}
                    </div>
                    <button onClick={() => openFileInBrowser(f)}
                      style={{ ...btnPrimary, width: '100%', padding: '10px 0', fontSize: 13, marginTop: 18 }}>
                      Open / Preview
                    </button>
                  </div>
                )}

                {fileActionTab === 'assign' && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Assign to Patient</div>
                    {/* Duration picker */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>Access Duration</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {DURATION_OPTIONS.map(d => (
                          <button key={d.value} onClick={() => setAssignDuration(d.value)} style={{
                            padding: '5px 10px', borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                            background: assignDuration === d.value ? '#eef2ff' : '#f9fafb',
                            color: assignDuration === d.value ? '#4f46e5' : '#9ca3af',
                            border: assignDuration === d.value ? '2px solid #a5b4fc' : '1.5px solid #e5e7eb',
                            transition: 'all 0.15s',
                          }}>{d.label}</button>
                        ))}
                      </div>
                    </div>
                    <input value={patientQuery} onChange={e => searchPatients(e.target.value)}
                      placeholder="Type patient name or card ID..."
                      style={{ ...inputStyle, width: '100%', marginBottom: 10, padding: '10px 14px' }} />
                    {searchingPt && <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 8 }}>Searching...</div>}
                    {patientResults.length > 0 && (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                        {patientResults.map((p, i) => {
                          const assigned = fAssignCards.includes(p.patientCard);
                          return (
                            <div key={p.patientCard} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '10px 14px', background: assigned ? '#f0fdf4' : (i % 2 ? '#fafafa' : '#fff'),
                              borderBottom: i < patientResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                            }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                                <div style={{ fontSize: 11, color: '#6b7280' }}>{p.patientCard}</div>
                              </div>
                              <button onClick={() => assigned ? handleFileUnassign(f._id, p.patientCard) : handleFileAssign(f._id, p.patientCard)}
                                style={{
                                  padding: '6px 14px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  background: assigned ? '#fef2f2' : '#eef2ff',
                                  color: assigned ? '#dc2626' : '#4f46e5',
                                  border: `1.5px solid ${assigned ? '#fca5a5' : '#a5b4fc'}`,
                                }}>{assigned ? 'Remove' : 'Assign'}</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {fileActionTab === 'keywords' && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Edit Keywords</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                      {(f.keywords || []).map(kw => (
                        <span key={kw} style={{ background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>{kw}</span>
                      ))}
                    </div>
                    <input value={kwInput} onChange={e => setKwInput(e.target.value)}
                      placeholder="yoga, document, therapy..." style={{ ...inputStyle, width: '100%', marginBottom: 12, padding: '10px 14px' }} />
                    <button onClick={async () => {
                      try {
                        const res = await api.put(`/resource-files/${f._id}`, { keywords: kwInput });
                        setResFiles(prev => prev.map(x => x._id === f._id ? res.data : x));
                        setFileActionTab('info');
                      } catch {}
                    }} style={{ ...btnPrimary, width: '100%', padding: '10px 0', fontSize: 13 }}>Update Keywords</button>
                  </div>
                )}

                {fileActionTab === 'delete' && (
                  <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 26 }}>!</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Delete this file?</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                      "{f.title || f.filename}" will be permanently removed from Google Drive.
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18 }}>
                      <button onClick={() => setFileActionTab('info')} style={{ ...btnOutline, padding: '10px 24px', fontSize: 13 }}>Cancel</button>
                      <button onClick={() => handleDeleteFile(f)}
                        style={{ ...btnPrimary, background: '#dc2626', padding: '10px 24px', fontSize: 13 }}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ SINGLE-VIDEO ACTION MODAL ═══ */}
      {actionVideo && (() => {
        const v = liveActionVideo || actionVideo;
        const isSaved = v.isSaved;
        const assignCards = isSaved ? getAssignedCards(v.assignedPatients) : [];
        const isShort = v.videoType === 'short';

        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
          }} onClick={e => { if (e.target === e.currentTarget) setActionVideo(null); }}>
            <div style={{
              background: '#fff', borderRadius: 20, maxWidth: 520, width: '94%', maxHeight: '88vh',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.25), 0 0 0 1px rgba(0,0,0,.05)',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Header */}
              <div style={{ position: 'relative', background: '#0f172a' }}>
                <div style={{
                  height: 120, backgroundImage: v.thumbnail ? `url(${v.thumbnail})` : 'none',
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  filter: 'blur(8px) brightness(0.4)', transform: 'scale(1.1)',
                }} />
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
                  padding: '16px 20px', background: 'linear-gradient(transparent 0%, rgba(15,23,42,.85) 100%)',
                }}>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                    {v.thumbnail && (
                      <img src={v.thumbnail} alt="" style={{
                        width: 72, height: 50, objectFit: 'cover', borderRadius: 8,
                        border: '2px solid rgba(255,255,255,.3)', flexShrink: 0,
                      }} />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{v.title || 'Untitled'}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>
                        {isShort ? 'Short' : 'Video'}{v.publishedAt ? ` · ${fmtDate(v.publishedAt)}` : ''}
                        {isSaved && <span style={{ color: '#a5b4fc' }}> · Saved</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setActionVideo(null)} style={{
                    background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
                    width: 32, height: 32, borderRadius: '50%', fontSize: 16, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>x</button>
                </div>
              </div>

              {/* Tabs */}
              {isSaved && (
                <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 8px', background: '#fafafa' }}>
                  {[
                    { key: 'info', label: 'Overview' }, { key: 'assign', label: 'Assign' },
                    { key: 'tags', label: 'Body Parts' }, { key: 'keywords', label: 'Keywords' },
                    { key: 'delete', label: 'Delete' },
                  ].map(t => (
                    <button key={t.key} onClick={() => setActionTab(t.key)} style={{
                      padding: '10px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      background: 'none', border: 'none',
                      color: actionTab === t.key ? (t.key === 'delete' ? '#dc2626' : '#4f46e5') : '#6b7280',
                      borderBottom: actionTab === t.key ? `2.5px solid ${t.key === 'delete' ? '#dc2626' : '#4f46e5'}` : '2.5px solid transparent',
                    }}>{t.label}</button>
                  ))}
                </div>
              )}

              {/* Content */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                {actionTab === 'save' && !isSaved && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>Save to Library</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Add keywords to save this video and unlock assignment, tagging, and more.</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Keywords *</div>
                    <input value={saveKeywordsInput[v.youtubeId] || ''}
                      onChange={e => setSaveKeywordsInput(prev => ({ ...prev, [v.youtubeId]: e.target.value }))}
                      placeholder="yoga, back pain, stretch (comma separated)"
                      style={{ ...kwInputStyle, marginBottom: 16 }}
                      onKeyDown={e => e.key === 'Enter' && handleSave(v)} />
                    <button onClick={() => handleSave(v)} disabled={savingId === v.youtubeId}
                      style={{ ...btnPrimary, width: '100%', padding: '10px 0', fontSize: 13 }}>
                      {savingId === v.youtubeId ? 'Saving...' : 'Save to Library'}</button>
                  </div>
                )}

                {actionTab === 'info' && isSaved && (
                  <div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Keywords</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(v.keywords || []).length > 0 ? v.keywords.map(kw => (
                          <span key={kw} style={{ background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{kw}</span>
                        )) : <span style={{ color: '#9ca3af', fontSize: 12 }}>None</span>}
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Body Parts</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(v.bodyParts || []).length > 0 ? v.bodyParts.map(tag => {
                          const opt = BODY_PART_OPTIONS.find(o => o.key === tag);
                          return <span key={tag} style={{ background: '#ede9fe', color: '#4f46e5', borderRadius: 10, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{opt?.label || tag}</span>;
                        }) : <span style={{ color: '#9ca3af', fontSize: 12 }}>None</span>}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Assigned Patients</div>
                      {assignCards.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {assignCards.map(card => {
                            const dt = getAssignedDate(v.assignedPatients, card);
                            var ae = getAssignmentEntry(v.assignedPatients, card);
                            var expLabel = ae ? fmtExpiry(ae.validUntil) : null;
                            return (
                              <div key={card} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '6px 10px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0',
                              }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>{card}</span>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  {expLabel && <span style={{ fontSize: 10, color: expLabel === 'Expired' ? '#dc2626' : '#6b7280' }}>{expLabel}</span>}
                                  <span style={{ fontSize: 10, color: '#6b7280' }}>{dt ? fmtDate(dt) : ''}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>Not assigned</span>}
                    </div>
                  </div>
                )}

                {actionTab === 'assign' && isSaved && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Assign to Patient</div>
                    {/* Label picker */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                      {[{ k: 'mandatory', icon: '\uD83D\uDD34', c: '#dc2626', bg: '#fef2f2', bc: '#fca5a5' },
                        { k: 'recommended', icon: '\uD83D\uDFE1', c: '#d97706', bg: '#fffbeb', bc: '#fcd34d' },
                        { k: 'optional', icon: '\uD83D\uDFE2', c: '#16a34a', bg: '#f0fdf4', bc: '#86efac' }].map(o => (
                        <button key={o.k} onClick={() => setAssignLabel(o.k)} style={{
                          flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          background: assignLabel === o.k ? o.bg : '#f9fafb', color: assignLabel === o.k ? o.c : '#9ca3af',
                          border: `2px solid ${assignLabel === o.k ? o.bc : '#e5e7eb'}`, transition: 'all 0.15s',
                        }}>{o.icon} {o.k[0].toUpperCase() + o.k.slice(1)}</button>
                      ))}
                    </div>
                    {/* Duration picker */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>Access Duration</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {DURATION_OPTIONS.map(d => (
                          <button key={d.value} onClick={() => setAssignDuration(d.value)} style={{
                            padding: '5px 10px', borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                            background: assignDuration === d.value ? '#eef2ff' : '#f9fafb',
                            color: assignDuration === d.value ? '#4f46e5' : '#9ca3af',
                            border: assignDuration === d.value ? '2px solid #a5b4fc' : '1.5px solid #e5e7eb',
                            transition: 'all 0.15s',
                          }}>{d.label}</button>
                        ))}
                      </div>
                    </div>
                    {/* Step indicator */}
                    <div style={{
                      background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                      padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#1e40af', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{ fontSize: 16 }}>{'\uD83D\uDD0D'}</span>
                      Search patient below, then tap <span style={{ background: '#059669', color: '#fff', padding: '1px 8px', borderRadius: 4, fontSize: 11 }}>Assign</span> to assign with above settings
                    </div>
                    <input value={patientQuery} onChange={e => searchPatients(e.target.value)}
                      placeholder="Type patient name, card ID, or phone..."
                      style={{ ...inputStyle, width: '100%', marginBottom: 10, padding: '10px 14px', border: '2px solid #a5b4fc', borderRadius: 10 }} />
                    {searchingPt && <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 8 }}>Searching...</div>}
                    {!searchingPt && !patientQuery && patientResults.length === 0 && (
                      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '8px 0', marginBottom: 10 }}>
                        Start typing to search patients...
                      </div>
                    )}
                    {patientResults.length > 0 && (
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                        {patientResults.map((p, i) => {
                          const assigned = assignCards.includes(p.patientCard);
                          return (
                            <div key={p.patientCard} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '10px 14px', background: assigned ? '#f0fdf4' : (i % 2 ? '#fafafa' : '#fff'),
                              borderBottom: i < patientResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                            }}>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                                <div style={{ fontSize: 11, color: '#6b7280' }}>{p.patientCard}{p.number ? ' \u00B7 ' + p.number : ''}</div>
                              </div>
                              <button onClick={() => assigned ? handleUnassign(v._id, p.patientCard) : handleAssign(v._id, p.patientCard)}
                                style={{
                                  padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                  background: assigned ? '#fef2f2' : '#059669',
                                  color: assigned ? '#dc2626' : '#fff',
                                  border: assigned ? '1.5px solid #fca5a5' : 'none',
                                  boxShadow: assigned ? 'none' : '0 2px 8px rgba(5,150,105,0.3)',
                                }}>{assigned ? 'Remove' : '\u2714 Assign'}</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {assignCards.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>Currently Assigned ({assignCards.length})</div>
                        {assignCards.map(card => {
                          var ae = getAssignmentEntry(v.assignedPatients, card);
                          var expLabel = ae ? fmtExpiry(ae.validUntil) : null;
                          var labelColors = { mandatory: '#dc2626', recommended: '#d97706', optional: '#16a34a' };
                          var labelIcons = { mandatory: '\uD83D\uDD34', recommended: '\uD83D\uDFE1', optional: '\uD83D\uDFE2' };
                          return (
                          <div key={card} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', marginBottom: 4,
                          }}>
                            <div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>{card}</span>
                              {ae && ae.label && <span style={{ fontSize: 10, color: labelColors[ae.label] || '#6b7280', marginLeft: 6, fontWeight: 700 }}>{labelIcons[ae.label] || ''} {ae.label}</span>}
                              {ae && ae.durationDays && <span style={{ fontSize: 10, color: '#4f46e5', marginLeft: 6 }}>{ae.durationDays}d</span>}
                              {expLabel && <span style={{ fontSize: 10, color: expLabel === 'Expired' ? '#dc2626' : '#6b7280', marginLeft: 6 }}>{expLabel}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <select onChange={e => { if (e.target.value) handleExtendVideo(v._id, card, parseInt(e.target.value)); e.target.value = ''; }}
                                defaultValue="" style={{ padding: '3px 6px', borderRadius: 6, fontSize: 10, border: '1px solid #a5b4fc', color: '#4f46e5', cursor: 'pointer', background: '#eef2ff' }}>
                                <option value="" disabled>Extend</option>
                                {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                              </select>
                              <button onClick={() => handleUnassign(v._id, card)} style={{
                                padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                              }}>Remove</button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {actionTab === 'tags' && isSaved && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 16 }}>Body Parts</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                      {BODY_PART_OPTIONS.map(opt => {
                        const active = (v.bodyParts || []).includes(opt.key);
                        return (
                          <button key={opt.key} onClick={() => handleTagToggle(v._id, opt.key, v.bodyParts || [])}
                            style={{
                              padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                              background: active ? '#ede9fe' : '#f9fafb', color: active ? '#4f46e5' : '#374151',
                              border: active ? '2px solid #a78bfa' : '1.5px solid #e5e7eb', transition: 'all 0.12s',
                            }}>
                            <span style={{
                              width: 20, height: 20, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: active ? '#4f46e5' : '#e5e7eb', color: '#fff', fontSize: 12, fontWeight: 800,
                            }}>{active ? '\u2713' : ''}</span>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {actionTab === 'keywords' && isSaved && (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 14 }}>Edit Keywords</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                      {(v.keywords || []).map(kw => (
                        <span key={kw} style={{ background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>{kw}</span>
                      ))}
                    </div>
                    <input value={kwInput} onChange={e => setKwInput(e.target.value)}
                      placeholder="yoga, back, stretch, therapy..."
                      style={{ ...inputStyle, width: '100%', marginBottom: 12, padding: '10px 14px' }}
                      onKeyDown={e => e.key === 'Enter' && handleKeywordsUpdate(v._id)} />
                    <button onClick={() => handleKeywordsUpdate(v._id)}
                      style={{ ...btnPrimary, width: '100%', padding: '10px 0', fontSize: 13 }}>Update Keywords</button>
                  </div>
                )}

                {actionTab === 'delete' && isSaved && (
                  <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%', background: '#fef2f2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px', fontSize: 26,
                    }}>!</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Delete this video?</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6, lineHeight: 1.5 }}>
                      "{v.title}" will be permanently removed.
                    </div>
                    {assignCards.length > 0 && (
                      <div style={{
                        padding: '10px 14px', background: '#fff7ed', borderRadius: 10, border: '1px solid #fed7aa',
                        fontSize: 12, color: '#9a3412', marginBottom: 8,
                      }}>Assigned to <strong>{assignCards.length} patient(s)</strong>. Deleting will unassign.</div>
                    )}
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18 }}>
                      <button onClick={() => setActionTab('info')} style={{ ...btnOutline, padding: '10px 24px', fontSize: 13 }}>Cancel</button>
                      <button onClick={() => handleDeleteVideo(v)}
                        style={{ ...btnPrimary, background: '#dc2626', padding: '10px 24px', fontSize: 13 }}>
                        {assignCards.length > 0 ? 'Delete & Unassign' : 'Delete'}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ BULK MODALS ═══ */}
      {bulkModal && (() => {
        const n = selectedIds.size;

        const ModalShell = ({ children, title, icon, iconBg }) => (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9100,
          }} onClick={e => { if (e.target === e.currentTarget && !bulkBusy) setBulkModal(null); }}>
            <div style={{
              background: '#fff', borderRadius: 20, maxWidth: 500, width: '94%', maxHeight: '85vh',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
              display: 'flex', flexDirection: 'column',
            }}>
              {/* Modal header */}
              <div style={{
                padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: iconBg || '#eef2ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
                }}>{icon}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{title}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{n} video{n > 1 ? 's' : ''} selected</div>
                </div>
                <button onClick={() => !bulkBusy && setBulkModal(null)} style={{
                  background: '#f3f4f6', border: 'none', width: 32, height: 32, borderRadius: '50%',
                  fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6b7280',
                }}>x</button>
              </div>
              {/* Modal body */}
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                {children}
              </div>
            </div>
          </div>
        );

        // ── Add Keywords Modal ──
        if (bulkModal === 'addKeywords') {
          const previewKws = bulkKwInput.split(/[,\s]+/).map(k => k.trim().toLowerCase()).filter(Boolean);
          return (
            <ModalShell title="Add Keywords" icon="+" iconBg="#eef2ff">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                These keywords will be appended to all {n} selected videos.
              </div>
              <input value={bulkKwInput} onChange={e => setBulkKwInput(e.target.value)}
                placeholder="yoga, therapy, stretch (comma separated)"
                style={{ ...inputStyle, width: '100%', padding: '10px 14px', marginBottom: 10 }}
                onKeyDown={e => e.key === 'Enter' && handleBulkAddKeywords()} />
              {previewKws.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                  {previewKws.map(kw => (
                    <span key={kw} style={{ background: '#dcfce7', color: '#166534', borderRadius: 10, padding: '4px 10px', fontSize: 11, fontWeight: 600 }}>+ {kw}</span>
                  ))}
                </div>
              )}
              <button onClick={handleBulkAddKeywords} disabled={bulkBusy || previewKws.length === 0}
                style={{ ...btnPrimary, width: '100%', padding: '10px 0', fontSize: 13, opacity: previewKws.length === 0 ? .5 : 1 }}>
                {bulkBusy ? 'Adding...' : `Add Keywords to ${n} Videos`}</button>
            </ModalShell>
          );
        }

        // ── Remove Keywords Modal ──
        if (bulkModal === 'removeKeywords') {
          return (
            <ModalShell title="Remove Keywords" icon="-" iconBg="#fffbeb">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
                Tap keywords to mark them for removal from all {n} selected videos.
              </div>
              {allSelectedKeywords.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No keywords found on selected videos.</div>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                  {allSelectedKeywords.map(kw => {
                    const marked = bulkRemoveKws.has(kw);
                    return (
                      <button key={kw} onClick={() => {
                        setBulkRemoveKws(prev => {
                          const next = new Set(prev);
                          if (next.has(kw)) next.delete(kw); else next.add(kw);
                          return next;
                        });
                      }} style={{
                        padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: marked ? '#fef2f2' : '#fef3c7',
                        color: marked ? '#dc2626' : '#92400e',
                        border: marked ? '2px solid #fca5a5' : '1.5px solid #fde68a',
                        textDecoration: marked ? 'line-through' : 'none',
                        transition: 'all 0.12s',
                      }}>{kw}</button>
                    );
                  })}
                </div>
              )}
              <button onClick={handleBulkRemoveKeywords} disabled={bulkBusy || bulkRemoveKws.size === 0}
                style={{ ...btnPrimary, width: '100%', padding: '10px 0', fontSize: 13, background: '#b45309', opacity: bulkRemoveKws.size === 0 ? .5 : 1 }}>
                {bulkBusy ? 'Removing...' : `Remove ${bulkRemoveKws.size} Keyword${bulkRemoveKws.size !== 1 ? 's' : ''}`}</button>
            </ModalShell>
          );
        }

        // ── Assign Modal ──
        if (bulkModal === 'assign') {
          return (
            <ModalShell title="Assign to Patient" icon={'\u21D2'} iconBg="#ecfdf5">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Search for a patient to assign all {n} selected videos to them.
              </div>
              {/* Label picker */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[{ k: 'mandatory', icon: '\uD83D\uDD34', c: '#dc2626', bg: '#fef2f2', bc: '#fca5a5' },
                  { k: 'recommended', icon: '\uD83D\uDFE1', c: '#d97706', bg: '#fffbeb', bc: '#fcd34d' },
                  { k: 'optional', icon: '\uD83D\uDFE2', c: '#16a34a', bg: '#f0fdf4', bc: '#86efac' }].map(o => (
                  <button key={o.k} onClick={() => setAssignLabel(o.k)} style={{
                    flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    background: assignLabel === o.k ? o.bg : '#f9fafb', color: assignLabel === o.k ? o.c : '#9ca3af',
                    border: `2px solid ${assignLabel === o.k ? o.bc : '#e5e7eb'}`, transition: 'all 0.15s',
                  }}>{o.icon} {o.k[0].toUpperCase() + o.k.slice(1)}</button>
                ))}
              </div>
              {/* Duration picker */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>Access Duration</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {DURATION_OPTIONS.map(d => (
                    <button key={d.value} onClick={() => setAssignDuration(d.value)} style={{
                      padding: '5px 10px', borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                      background: assignDuration === d.value ? '#eef2ff' : '#f9fafb',
                      color: assignDuration === d.value ? '#4f46e5' : '#9ca3af',
                      border: assignDuration === d.value ? '2px solid #a5b4fc' : '1.5px solid #e5e7eb',
                      transition: 'all 0.15s',
                    }}>{d.label}</button>
                  ))}
                </div>
              </div>
              <input value={patientQuery} onChange={e => searchPatients(e.target.value)}
                placeholder="Type patient name or card ID..."
                style={{ ...inputStyle, width: '100%', padding: '10px 14px', marginBottom: 10 }} />
              {searchingPt && <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 8 }}>Searching...</div>}
              {patientResults.length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  {patientResults.map((p, i) => (
                    <div key={p.patientCard} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', background: i % 2 ? '#fafafa' : '#fff',
                      borderBottom: i < patientResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{p.patientCard}</div>
                      </div>
                      <button onClick={() => handleBulkAssign(p.patientCard)} disabled={bulkBusy}
                        style={{
                          padding: '6px 16px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          background: '#059669', color: '#fff', border: 'none',
                        }}>{bulkBusy ? '...' : 'Assign'}</button>
                    </div>
                  ))}
                </div>
              )}
            </ModalShell>
          );
        }

        // ── Unassign Modal ──
        if (bulkModal === 'unassign') {
          return (
            <ModalShell title="Remove Patient Assignment" icon={'\u21D0'} iconBg="#fffbeb">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Search for a patient to remove their assignment from all {n} selected videos.
              </div>
              <input value={patientQuery} onChange={e => searchPatients(e.target.value)}
                placeholder="Type patient name or card ID..."
                style={{ ...inputStyle, width: '100%', padding: '10px 14px', marginBottom: 10 }} />
              {searchingPt && <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 8 }}>Searching...</div>}
              {patientResults.length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  {patientResults.map((p, i) => (
                    <div key={p.patientCard} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', background: i % 2 ? '#fafafa' : '#fff',
                      borderBottom: i < patientResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{p.patientCard}</div>
                      </div>
                      <button onClick={() => handleBulkUnassign(p.patientCard)} disabled={bulkBusy}
                        style={{
                          padding: '6px 16px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          background: '#d97706', color: '#fff', border: 'none',
                        }}>{bulkBusy ? '...' : 'Remove'}</button>
                    </div>
                  ))}
                </div>
              )}
            </ModalShell>
          );
        }

        // ── Delete Modal ──
        if (bulkModal === 'delete') {
          return (
            <ModalShell title="Delete Videos" icon="!" iconBg="#fef2f2">
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', background: '#fef2f2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px', fontSize: 30, color: '#dc2626', fontWeight: 800,
                  border: '3px solid #fca5a5',
                }}>!</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                  Delete {n} video{n > 1 ? 's' : ''}?
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 8 }}>
                  This action is permanent and cannot be undone.
                </div>
                {selectedAssignedCount > 0 && (
                  <div style={{
                    padding: '10px 14px', background: '#fff7ed', borderRadius: 10, border: '1px solid #fed7aa',
                    fontSize: 12, color: '#9a3412', marginBottom: 8, lineHeight: 1.5,
                  }}>
                    <strong>{selectedAssignedCount}</strong> of these are assigned to patients. Deleting will unassign them.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
                  <button onClick={() => setBulkModal(null)} disabled={bulkBusy}
                    style={{ ...btnOutline, padding: '10px 28px', fontSize: 13 }}>Cancel</button>
                  <button onClick={handleBulkDelete} disabled={bulkBusy}
                    style={{ ...btnPrimary, background: '#dc2626', padding: '10px 28px', fontSize: 13 }}>
                    {bulkBusy ? 'Deleting...' : `Delete ${n} Video${n > 1 ? 's' : ''}`}</button>
                </div>
              </div>
            </ModalShell>
          );
        }

        return null;
      })()}

      {/* ═══ ADD MODAL ═══ */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000 }}
          onClick={e => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 560, width: '94%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Add Channel or Video</h3>
              <button onClick={() => setShowAddModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', padding: '0 4px' }}>x</button>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
              {[
                { key: 'channel', label: 'YouTube Channel' }, { key: 'playlist', label: 'Playlist' },
                { key: 'youtube', label: 'YouTube URL' },
                { key: 'drive', label: 'Drive Link' }, { key: 'upload', label: 'Upload File' },
              ].map(t => (
                <button key={t.key} onClick={() => setAddTab(t.key)} style={{
                  padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  background: addTab === t.key ? '#4f46e5' : '#f3f4f6',
                  color: addTab === t.key ? '#fff' : '#374151',
                  border: addTab === t.key ? '1.5px solid #4f46e5' : '1.5px solid #d1d5db',
                }}>{t.label}</button>
              ))}
            </div>

            {addTab === 'channel' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Paste a YouTube channel URL or channel ID</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>youtube.com/@handle, youtube.com/channel/UC..., or direct ID</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={channelUrl} onChange={e => setChannelUrl(e.target.value)}
                    placeholder="e.g. https://www.youtube.com/@MedicalYogaCentre"
                    style={inputStyle} onKeyDown={e => e.key === 'Enter' && handleAddChannel()} />
                  <button onClick={handleAddChannel} disabled={addingChannel || !channelUrl.trim()} style={btnPrimary}>
                    {addingChannel ? 'Adding...' : 'Add Channel'}</button>
                </div>
                {addingChannel && <div style={{ fontSize: 11, color: '#6366f1', marginTop: 8 }}>Resolving channel...</div>}
                {channels.length > 0 && (
                  <div style={{ marginTop: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Linked channels ({channels.length}):</div>
                    {channels.map(ch => (
                      <div key={ch._id} style={{ fontSize: 12, color: '#1e293b', padding: '3px 0' }}>
                        {ch.name || ch.channelId} <span style={{ color: '#9ca3af', fontSize: 10 }}>({ch.channelId})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {addTab === 'playlist' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Paste a YouTube Playlist URL</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>e.g. https://www.youtube.com/playlist?list=PLxxxxxx</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={playlistUrl} onChange={e => setPlaylistUrl(e.target.value)}
                    placeholder="https://www.youtube.com/playlist?list=..."
                    style={inputStyle} onKeyDown={e => e.key === 'Enter' && handleAddPlaylist()} />
                  <button onClick={handleAddPlaylist} disabled={addingPlaylist || !playlistUrl.trim()} style={btnPrimary}>
                    {addingPlaylist ? 'Importing...' : 'Import Playlist'}</button>
                </div>
                {playlistImportMsg && (
                  <div style={{ fontSize: 12, color: '#059669', marginTop: 10, padding: '8px 12px', background: '#ecfdf5', borderRadius: 8 }}>
                    {playlistImportMsg}
                  </div>
                )}
                {playlists.length > 0 && (
                  <div style={{ marginTop: 16, padding: '12px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Imported playlists ({playlists.length}):</div>
                    {playlists.map(pl => (
                      <div key={pl._id} style={{ fontSize: 12, color: '#1e293b', padding: '3px 0' }}>
                        {pl.title || pl.playlistId} <span style={{ color: '#9ca3af', fontSize: 10 }}>({pl.videoCount} videos)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {addTab === 'youtube' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>YouTube Video URL</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Shorts URLs (youtube.com/shorts/...) are auto-detected.</div>
                <input value={ytUrl} onChange={e => setYtUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... or /shorts/..." style={{ ...inputStyle, width: '100%', marginBottom: 10 }}
                  onKeyDown={e => e.key === 'Enter' && handleAddYoutubeUrl()} />
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Keywords *</div>
                <input value={ytKeywords} onChange={e => setYtKeywords(e.target.value)}
                  placeholder="yoga, back pain, stretch" style={{ ...kwInputStyle, marginBottom: 12 }} />
                <button onClick={handleAddYoutubeUrl} disabled={ytAdding || !ytUrl.trim()} style={{ ...btnPrimary, width: '100%' }}>
                  {ytAdding ? 'Adding...' : 'Add Video'}</button>
              </div>
            )}

            {addTab === 'drive' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Title</div>
                <input value={driveTitle} onChange={e => setDriveTitle(e.target.value)} placeholder="Video title (optional)" style={{ ...inputStyle, width: '100%', marginBottom: 10 }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Google Drive Link</div>
                <input value={driveUrl} onChange={e => setDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/file/d/.../view" style={{ ...inputStyle, width: '100%', marginBottom: 10 }}
                  onKeyDown={e => e.key === 'Enter' && handleAddDriveLink()} />
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Keywords *</div>
                <input value={driveKeywords} onChange={e => setDriveKeywords(e.target.value)}
                  placeholder="yoga, back pain" style={{ ...kwInputStyle, marginBottom: 12 }} />
                <button onClick={handleAddDriveLink} disabled={driveAdding || !driveUrl.trim()} style={{ ...btnPrimary, width: '100%' }}>
                  {driveAdding ? 'Adding...' : 'Add Drive Video'}</button>
              </div>
            )}

            {addTab === 'upload' && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Title</div>
                <input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Video title (optional)" style={{ ...inputStyle, width: '100%', marginBottom: 10 }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Video File</div>
                <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                  onChange={e => setUploadFile(e.target.files?.[0] || null)} style={{ fontSize: 12, marginBottom: 10 }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Keywords *</div>
                <input value={uploadKeywords} onChange={e => setUploadKeywords(e.target.value)}
                  placeholder="yoga, back pain" style={{ ...kwInputStyle, marginBottom: 4 }} />
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>Max 100 MB. MP4, MOV, WebM, AVI</div>
                <button onClick={handleUpload} disabled={uploading || !uploadFile} style={{ ...btnPrimary, width: '100%' }}>
                  {uploading ? 'Uploading...' : 'Upload Video'}</button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ═══ PLAYLIST ASSIGN MODAL ═══ */}
      {plAssignModal && (() => {
        const pl = playlists.find(p => p._id === plAssignModal._id) || plAssignModal;
        const plAssignCards = getAssignedCards(pl.assignedPatients);

        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9100,
          }} onClick={e => { if (e.target === e.currentTarget) setPlAssignModal(null); }}>
            <div style={{
              background: '#fff', borderRadius: 20, maxWidth: 500, width: '94%', maxHeight: '85vh',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.25)',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                padding: '20px 24px 16px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', gap: 14,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: '#f5f3ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0,
                }}>{'\u25b6'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Assign Playlist</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    "{pl.title}" ({pl.videoCount} videos) — all videos will be assigned
                  </div>
                </div>
                <button onClick={() => setPlAssignModal(null)} style={{
                  background: '#f3f4f6', border: 'none', width: 32, height: 32, borderRadius: '50%',
                  fontSize: 16, cursor: 'pointer', color: '#6b7280',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>x</button>
              </div>
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                {/* Label picker */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {[{ k: 'mandatory', icon: '\uD83D\uDD34', c: '#dc2626', bg: '#fef2f2', bc: '#fca5a5' },
                    { k: 'recommended', icon: '\uD83D\uDFE1', c: '#d97706', bg: '#fffbeb', bc: '#fcd34d' },
                    { k: 'optional', icon: '\uD83D\uDFE2', c: '#16a34a', bg: '#f0fdf4', bc: '#86efac' }].map(o => (
                    <button key={o.k} onClick={() => setAssignLabel(o.k)} style={{
                      flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      background: assignLabel === o.k ? o.bg : '#f9fafb', color: assignLabel === o.k ? o.c : '#9ca3af',
                      border: `2px solid ${assignLabel === o.k ? o.bc : '#e5e7eb'}`, transition: 'all 0.15s',
                    }}>{o.icon} {o.k[0].toUpperCase() + o.k.slice(1)}</button>
                  ))}
                </div>
                {/* Sequential toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', marginBottom: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!pl.sequential}
                    onChange={async () => {
                      try {
                        const res = await api.put(`/youtube/playlists/${pl._id}/sequential`, { sequential: !pl.sequential });
                        setPlaylists(prev => prev.map(p => p._id === pl._id ? { ...p, sequential: res.data.sequential } : p));
                      } catch {}
                    }}
                    style={{ width: 16, height: 16 }} />
                  <span style={{ fontWeight: 600 }}>Sequential playlist</span>
                  <span style={{ color: '#9ca3af' }}>(patient must watch in order)</span>
                </label>
                {/* Duration picker */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>Access Duration</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {DURATION_OPTIONS.map(d => (
                      <button key={d.value} onClick={() => setAssignDuration(d.value)} style={{
                        padding: '5px 10px', borderRadius: 7, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        background: assignDuration === d.value ? '#eef2ff' : '#f9fafb',
                        color: assignDuration === d.value ? '#4f46e5' : '#9ca3af',
                        border: assignDuration === d.value ? '2px solid #a5b4fc' : '1.5px solid #e5e7eb',
                        transition: 'all 0.15s',
                      }}>{d.label}</button>
                    ))}
                  </div>
                </div>
                {/* Step indicator */}
                <div style={{
                  background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                  padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#1e40af', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ fontSize: 16 }}>{'\uD83D\uDD0D'}</span>
                  Search patient below, then tap <span style={{ background: '#059669', color: '#fff', padding: '1px 8px', borderRadius: 4, fontSize: 11 }}>Assign</span> to assign with above settings
                </div>
                <input value={patientQuery} onChange={e => searchPatients(e.target.value)}
                  placeholder="Type patient name, card ID, or phone..."
                  style={{ ...inputStyle, width: '100%', padding: '10px 14px', marginBottom: 10, border: '2px solid #a5b4fc', borderRadius: 10 }} />
                {searchingPt && <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 8 }}>Searching...</div>}
                {!searchingPt && !patientQuery && patientResults.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '8px 0', marginBottom: 10 }}>
                    Start typing to search patients...
                  </div>
                )}
                {patientResults.length > 0 && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                    {patientResults.map((p, i) => {
                      const assigned = plAssignCards.includes(p.patientCard);
                      return (
                        <div key={p.patientCard} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 14px', background: assigned ? '#f0fdf4' : (i % 2 ? '#fafafa' : '#fff'),
                          borderBottom: i < patientResults.length - 1 ? '1px solid #f3f4f6' : 'none',
                        }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{p.name}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{p.patientCard}{p.number ? ' \u00B7 ' + p.number : ''}</div>
                          </div>
                          <button onClick={() => assigned ? handlePlaylistUnassign(pl._id, p.patientCard) : handlePlaylistAssign(pl._id, p.patientCard)}
                            style={{
                              padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              background: assigned ? '#fef2f2' : '#059669',
                              color: assigned ? '#dc2626' : '#fff',
                              border: assigned ? '1.5px solid #fca5a5' : 'none',
                              boxShadow: assigned ? 'none' : '0 2px 8px rgba(5,150,105,0.3)',
                            }}>{assigned ? 'Remove' : '\u2714 Assign'}</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {plAssignCards.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 8 }}>
                      Currently Assigned ({plAssignCards.length})
                    </div>
                    {plAssignCards.map(card => {
                      var ae = getAssignmentEntry(pl.assignedPatients, card);
                      var expLabel = ae ? fmtExpiry(ae.validUntil) : null;
                      var labelColors = { mandatory: '#dc2626', recommended: '#d97706', optional: '#16a34a' };
                      var labelIcons = { mandatory: '\uD83D\uDD34', recommended: '\uD83D\uDFE1', optional: '\uD83D\uDFE2' };
                      return (
                      <div key={card} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', marginBottom: 4,
                      }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#166534' }}>{card}</span>
                          {ae && ae.label && <span style={{ fontSize: 10, color: labelColors[ae.label] || '#6b7280', marginLeft: 6, fontWeight: 700 }}>{labelIcons[ae.label] || ''} {ae.label}</span>}
                          {ae && ae.durationDays && <span style={{ fontSize: 10, color: '#4f46e5', marginLeft: 6 }}>{ae.durationDays}d</span>}
                          {expLabel && <span style={{ fontSize: 10, color: expLabel === 'Expired' ? '#dc2626' : '#6b7280', marginLeft: 6 }}>{expLabel}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <select onChange={e => { if (e.target.value) handlePlaylistExtend(pl._id, card, parseInt(e.target.value)); e.target.value = ''; }}
                            defaultValue="" style={{ padding: '3px 6px', borderRadius: 6, fontSize: 10, border: '1px solid #a5b4fc', color: '#4f46e5', cursor: 'pointer', background: '#eef2ff' }}>
                            <option value="" disabled>Extend</option>
                            {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                          </select>
                          <button onClick={() => handlePlaylistUnassign(pl._id, card)} style={{
                            padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                            background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                          }}>Remove</button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════════
           UPLOAD FILE MODAL
         ═══════════════════════════════════════════════════════════════════════ */}
      {showUploadDocModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,.45)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={() => { if (!uploadingDoc) setShowUploadDocModal(false); }}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
            boxShadow: '0 24px 80px rgba(0,0,0,.25)', overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Upload File</div>
              <button onClick={() => { if (!uploadingDoc) setShowUploadDocModal(false); }}
                style={{ background: 'none', border: 'none', fontSize: 20, color: '#6b7280', cursor: 'pointer', padding: 4, lineHeight: 1 }}>x</button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>File</label>
                <input ref={docFileRef} type="file" onChange={e => setUploadDocFile(e.target.files?.[0] || null)}
                  style={{ fontSize: 13, width: '100%' }} />
                {uploadDocFile && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    {uploadDocFile.name} ({(uploadDocFile.size / 1048576).toFixed(1)} MB)
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Title (optional)</label>
                <input value={uploadDocTitle} onChange={e => setUploadDocTitle(e.target.value)}
                  placeholder="E.g. Yoga exercise chart"
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Keywords (optional)</label>
                <input value={uploadDocKw} onChange={e => setUploadDocKw(e.target.value)}
                  placeholder="yoga, chart, therapy..."
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              {/* Duplicate confirmation */}
              {dupCheckResult && dupCheckResult.exists && (
                <div style={{ marginBottom: 14, padding: '12px 14px', background: '#fffbeb', border: '1.5px solid #fbbf24', borderRadius: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>File already exists!</div>
                  <div style={{ fontSize: 12, color: '#78716c', marginBottom: 10 }}>
                    A file named "<strong>{uploadDocFile?.name}</strong>" already exists (uploaded {fmtDate(dupCheckResult.file?.uploadedAt)}).
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleUploadDoc('replace')}
                      style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid #dc2626', background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Replace
                    </button>
                    <button onClick={() => handleUploadDoc('copy')}
                      style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1.5px solid #4f46e5', background: '#eef2ff', color: '#4f46e5', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Add as Copy
                    </button>
                    <button onClick={() => setDupCheckResult(null)}
                      style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', background: '#f9fafb', color: '#6b7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <button onClick={() => handleUploadDoc()} disabled={uploadingDoc || !uploadDocFile}
                style={{
                  ...btnPrimary, width: '100%', padding: '12px 0', fontSize: 14,
                  opacity: uploadDocFile ? 1 : .5,
                }}>
                {uploadingDoc ? 'Uploading...' : 'Upload File'}
              </button>
              <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 10 }}>
                Max 50 MB. Files are encrypted on upload.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
           ADD LINK MODAL
         ═══════════════════════════════════════════════════════════════════════ */}
      {showAddLinkModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,.45)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }} onClick={() => { if (!addingLink) setShowAddLinkModal(false); }}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
            boxShadow: '0 24px 80px rgba(0,0,0,.25)', overflow: 'hidden',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Add Link</div>
              <button onClick={() => { if (!addingLink) setShowAddLinkModal(false); }}
                style={{ background: 'none', border: 'none', fontSize: 20, color: '#6b7280', cursor: 'pointer', padding: 4, lineHeight: 1 }}>x</button>
            </div>
            {/* Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>URL *</label>
                <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
                  placeholder="https://drive.google.com/file/d/... or any web link"
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Title *</label>
                <input value={linkTitle} onChange={e => setLinkTitle(e.target.value)}
                  placeholder="E.g. Yoga Exercise Book - PDF"
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Description (optional)</label>
                <input value={linkDesc} onChange={e => setLinkDesc(e.target.value)}
                  placeholder="Brief description of the resource"
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>Keywords (optional)</label>
                <input value={linkKeywords} onChange={e => setLinkKeywords(e.target.value)}
                  placeholder="book, yoga, therapy..."
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
              <button onClick={async () => {
                if (!linkUrl.trim() || !linkTitle.trim()) return;
                setAddingLink(true);
                try {
                  const res = await api.post('/resource-files/link', {
                    url: linkUrl.trim(), title: linkTitle.trim(),
                    description: linkDesc.trim(), keywords: linkKeywords,
                    folderPath: currentFolderPath,
                  });
                  setResFiles(prev => [res.data, ...prev]);
                  setShowAddLinkModal(false);
                  setLinkUrl(''); setLinkTitle(''); setLinkDesc(''); setLinkKeywords('');
                } catch (err) {
                  alert(err.response?.data?.error || 'Failed to add link');
                }
                setAddingLink(false);
              }} disabled={addingLink || !linkUrl.trim() || !linkTitle.trim()}
                style={{
                  ...btnPrimary, width: '100%', padding: '12px 0', fontSize: 14, background: '#0891b2',
                  opacity: (linkUrl.trim() && linkTitle.trim()) ? 1 : .5,
                }}>
                {addingLink ? 'Adding...' : 'Add Link'}
              </button>
              <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 10 }}>
                Links are saved as resources and can be assigned to patients.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
           MOVE FILES MODAL (folder picker)
         ═══════════════════════════════════════════════════════════════════════ */}
      {showMoveModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }} onClick={() => setShowMoveModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 16, padding: '24px 28px', width: '100%', maxWidth: 400,
            maxHeight: '70vh', overflow: 'auto',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              Move {selectedFileIds.size} file{selectedFileIds.size !== 1 ? 's' : ''} to...
            </h3>
            {/* Root option */}
            <div onClick={() => setMoveTargetPath('/')}
              style={{
                padding: '10px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 4,
                background: moveTargetPath === '/' ? '#eef2ff' : '#fafafa',
                border: moveTargetPath === '/' ? '1.5px solid #a5b4fc' : '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
              <span style={{ fontSize: 18 }}>🏠</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Root</span>
            </div>
            {/* All folders */}
            {allFolders.map(f => {
              const depth = (f.path.match(/\//g) || []).length;
              return (
                <div key={f.path} onClick={() => setMoveTargetPath(f.path)}
                  style={{
                    padding: '10px 14px', paddingLeft: 14 + (depth - 1) * 18, borderRadius: 10,
                    cursor: 'pointer', marginBottom: 4,
                    background: moveTargetPath === f.path ? '#eef2ff' : '#fafafa',
                    border: moveTargetPath === f.path ? '1.5px solid #a5b4fc' : '1px solid #f0f0f0',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  <span style={{ fontSize: 16 }}>📁</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{f.name}</span>
                </div>
              );
            })}
            {allFolders.length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 12, padding: '16px 0', textAlign: 'center' }}>
                No folders created yet. Files can only be moved to root.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={handleMoveFiles}
                style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Move Here
              </button>
              <button onClick={() => setShowMoveModal(false)}
                style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
